<template>
  <div class="dashboard-root" :class="themeClass">
    <div class="ambient" aria-hidden="true"></div>

    <header class="dash-header">
      <div class="brand" @click="$emit('home')" title="Inicio">
        <img src="@/assets/logogruas.png" alt="GM" class="logo" />
        <div class="brand-text">
          <h1>Panel Maestro</h1>
          <span class="brand-sub">Centro de Control · Grúas Monterrey</span>
        </div>
      </div>

      <div class="header-right">
        <div class="clock">
          <span class="clock-time">{{ clock.time }}</span>
          <span class="clock-date">{{ clock.date }}</span>
        </div>

        <div class="status-cluster">
          <span class="ws-pill" :class="wsClassOps"><span class="ws-dot"></span>Operaciones · {{ wsLabel(wsOps.status) }}</span>
          <span class="ws-pill" :class="wsClassAcc"><span class="ws-dot"></span>Contabilidad · {{ wsLabel(wsAcc.status) }}</span>
        </div>

        <button
          class="theme-toggle"
          type="button"
          @click="toggleTheme"
          :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
        >
          <svg v-if="isDark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </button>
      </div>
    </header>

    <nav class="tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        :class="['tab', { active: t.key===activeTab }]"
        @click="activeTab=t.key"
      >
        <svg class="tab-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <template v-if="t.key==='operations'"><path d="M3 12h4l3 8 4-16 3 8h4"/></template>
          <template v-else-if="t.key==='accounting'"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></template>
          <template v-else-if="t.key==='maintenance'"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z"/></template>
          <template v-else-if="t.key==='rrhh'"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></template>
          <template v-else-if="t.key==='estadistico'"><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="7"/><rect x="12" y="7" width="3" height="11"/><rect x="17" y="14" width="3" height="4"/></template>
          <template v-else><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></template>
        </svg>
        <span>{{ t.label }}</span>
      </button>
    </nav>

    <!-- OPERATIONS TAB -->
    <section v-if="activeTab==='operations'" :key="'ops-' + themeClass" class="tab-body">
      <div class="controls">
        <label>Fecha <input type="date" v-model="opsDate" @change="fetchOperations" /></label>
        <label>Mes <input type="month" v-model="opsMonth" @change="fetchOpsMonthly" /></label>
        <label>Año <input type="number" v-model.number="opsYear" @change="fetchOperationsMonthly" style="width:90px" /></label>
        <label>Comparar
          <select v-model="compareMode">
            <option value="none">Sin comparar</option>
            <option value="month">Mes anterior</option>
            <option value="year">Año anterior</option>
          </select>
        </label>
      </div>

      <div class="kpi-strip">
        <article v-for="k in opsKpis" :key="k.key" class="kpi-card" :class="{ 'is-alert': k.alert }">
          <div class="kpi-card__top">
            <span class="kpi-card__label">{{ k.label }}</span>
            <span v-if="k.alert" class="kpi-card__flag">Atención</span>
          </div>
          <div class="kpi-card__value">{{ k.value }}</div>
          <div class="kpi-card__meta">
            <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
            <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
          </div>
          <v-chart v-if="k.spark" class="kpi-spark" :option="k.spark" autoresize />
        </article>
      </div>

      <div class="chart-grid">
        <StatCard title="Órdenes por día">
          <v-chart :option="otDailyCountOption" autoresize style="height:260px" />
        </StatCard>
        <StatCard title="Subtotal por día">
          <v-chart :option="subtotalDailySumOption" autoresize style="height:260px" />
        </StatCard>
        <StatCard title="Órdenes por cliente">
          <v-chart :option="clientBarOption" autoresize :style="{ height: barHeight(clientBars.length) }" />
        </StatCard>
        <StatCard title="Órdenes por sucursal">
          <v-chart :option="sucursalCountOption" autoresize :style="{ height: barHeight(sucursalCountBars.length) }" />
        </StatCard>
        <StatCard title="Subtotal por sucursal">
          <v-chart :option="sucursalSubtotalOption" autoresize :style="{ height: barHeight(sucursalSubtotalBars.length) }" />
        </StatCard>
        <StatCard title="Frecuencia por tipo">
          <v-chart :option="typeFreqOption" autoresize :style="{ height: barHeight(typeBars.length) }" />
        </StatCard>
      </div>

      <section class="fleet">
        <div class="fleet__bar">
          <h2>Flota</h2>
          <div class="fleet__boards" role="tablist">
            <button type="button" :class="{ active: fleetBoard === 'gruas' }" @click="fleetBoard = 'gruas'">
              Grúas <span>{{ fleetCounts.gruas }}</span>
            </button>
            <button type="button" :class="{ active: fleetBoard === 'montacargas' }" @click="fleetBoard = 'montacargas'">
              Montacargas <span>{{ fleetCounts.montacargas }}</span>
            </button>
          </div>
          <span v-if="fleetLoading" class="muted">Cargando…</span>
          <span v-else-if="fleetError" class="quality-flag">{{ fleetError }}</span>
        </div>
        <div class="fleet__board">
          <div v-for="col in fleetColumns" :key="col.key" class="fleet__col" :class="`fleet__col--${col.key}`">
            <header>
              <span class="fleet__dot"></span>
              <span class="fleet__title">{{ col.title }}</span>
              <b>{{ col.units.length }}</b>
            </header>
            <div class="fleet__list">
              <article
                v-for="unit in col.units"
                :key="unit.id"
                class="fleet-card"
                :class="{ 'fleet-card--alert': fleetCardAlert(col.key, unit) }"
              >
                <div class="fleet-card__top">
                  <strong>{{ unit.unit_short_id }}</strong>
                  <span v-if="unit.unit_plates">{{ unit.unit_plates }}</span>
                </div>
                <p>{{ unit.operator_name || 'Sin operador asignado' }}</p>
                <template v-if="col.key === 'en_servicio'">
                  <p v-for="(svc, i) in unit.services" :key="i" class="fleet-card__svc">
                    <a v-if="svc.note_ident" :href="'/notes/' + svc.note_ident" target="_blank" rel="noopener">OT {{ svc.note_number }}</a>
                    <span v-else>{{ svc.note_number ? 'OT ' + svc.note_number : 'Montacargas' }}</span>
                    <span v-if="svc.note_status"> · {{ svc.note_status }}</span>
                    <span v-if="svc.start_time || svc.assigned_at"> · {{ elapsedLabel(svc.start_time || svc.assigned_at) }}</span>
                  </p>
                </template>
                <p v-else-if="col.key === 'mantenimiento'" class="fleet-card__time" :class="{ 'fleet-card__time--alert': fleetCardAlert(col.key, unit) }">
                  <template v-if="unit.maintenance_since">En mantenimiento {{ elapsedLabel(unit.maintenance_since) }} · {{ fmtDateTime(unit.maintenance_since) }}</template>
                  <template v-else>{{ unit.unit_status || 'Mantenimiento' }}</template>
                </p>
                <p v-else class="fleet-card__time" :class="{ 'fleet-card__time--alert': fleetCardAlert(col.key, unit) }">
                  <template v-if="unit.last_service_at">Último servicio {{ elapsedLabel(unit.last_service_at) }} · {{ fmtDateTime(unit.last_service_at) }}</template>
                  <template v-else>Sin servicios registrados</template>
                </p>
              </article>
              <p v-if="!col.units.length" class="muted fleet__empty">Sin unidades</p>
            </div>
          </div>
        </div>
      </section>

      <details class="fold" open>
        <summary>Servicios AXA del mes</summary>
        <div class="kpis">
          <div class="kpi"><div class="kpi-label">Aceptados</div><div class="kpi-value">{{ ops.insurerTotals.accepted || 0 }}</div></div>
          <div class="kpi"><div class="kpi-label">Tiempo agotado</div><div class="kpi-value">{{ ops.insurerTotals.timed_out || 0 }}</div></div>
          <div class="kpi"><div class="kpi-label">Con error</div><div class="kpi-value">{{ ops.insurerTotals.errored || 0 }}</div></div>
          <div class="kpi"><div class="kpi-label">Total</div><div class="kpi-value">{{ ops.insurerTotals.total || 0 }}</div></div>
        </div>
        <v-chart :option="insurerDailyOption" autoresize style="height:260px" />
      </details>

      <details class="fold" open>
        <summary>Acumulado del año</summary>
        <div class="chart-grid">
          <StatCard title="Órdenes por mes">
            <v-chart :option="opsMonthlyCountOption" autoresize style="height:240px" />
          </StatCard>
          <StatCard title="Subtotal por mes">
            <v-chart :option="opsMonthlySubtotalOption" autoresize style="height:240px" />
          </StatCard>
        </div>
      </details>
    </section>


    <!-- ACCOUNTING TAB -->
    <section v-else-if="activeTab==='accounting'" :key="'acc-' + themeClass" class="tab-body">
      <div class="controls">
        <label>Fecha <input type="date" v-model="accDate" @change="fetchAccounting" /></label>
        <label>Movimiento
          <select v-model.number="txType" @change="fetchAccounting">
            <option v-for="t in TX_TYPES" :key="t.id" :value="t.id">{{ t.label }}</option>
          </select>
        </label>
        <label>Comparar
          <select v-model="compareMode">
            <option value="none">Sin comparar</option>
            <option value="month">Mes anterior</option>
            <option value="year">Año anterior</option>
          </select>
        </label>
      </div>

      <div class="kpi-strip kpi-strip--three">
        <article v-for="k in accHeadlineKpis" :key="k.key" class="kpi-card" :class="{ 'is-alert': k.alert }">
          <div class="kpi-card__top">
            <span class="kpi-card__label">{{ k.label }}</span>
            <span v-if="k.alert" class="kpi-card__flag">Atención</span>
          </div>
          <div class="kpi-card__value">{{ k.value }}</div>
          <div class="kpi-card__meta">
            <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
            <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
          </div>
          <v-chart v-if="k.spark" class="kpi-spark" :option="k.spark" autoresize />
        </article>
      </div>
      <div class="kpi-strip kpi-strip--aging">
        <article v-for="k in accAgingKpis" :key="k.key" class="kpi-card" :class="{ 'is-alert': k.alert }">
          <div class="kpi-card__top">
            <span class="kpi-card__label">{{ k.label }}</span>
            <span v-if="k.alert" class="kpi-card__flag">Atención</span>
          </div>
          <div class="kpi-card__value">{{ k.value }}</div>
          <div class="kpi-card__meta">
            <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
            <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
          </div>
        </article>
      </div>

      <div class="chart-grid">
        <StatCard title="Saldo por cliente">
          <v-chart :option="balPerClientBarOption" autoresize :style="{ height: barHeight(balanceBars.length) }" />
        </StatCard>
        <StatCard title="Pendiente por facturar por cliente">
          <v-chart :option="pendingBillingByClientOption" autoresize :style="{ height: barHeight(pendingBars.length) }" />
        </StatCard>
        <StatCard title="Cartera del mes">
          <label class="inline-control">Mes <input type="month" v-model="creditMonth" @change="fetchCreditTimeseries" /></label>
          <v-chart :option="accCreditTimeseriesOption" autoresize style="height:260px" />
        </StatCard>
        <StatCard title="Pendiente por facturar por día">
          <label class="inline-control">Mes <input type="month" v-model="uninvoicedMonth" @change="fetchUninvoicedSubtotalDaily" /></label>
          <v-chart :option="accUninvoicedSubtotalDailyOption" autoresize style="height:260px" />
        </StatCard>
      </div>

      <div class="chart-grid">
        <StatCard :title="`Facturas del día (${invSummarySplit.count})`">
          <div class="kpis">
            <div class="kpi"><div class="kpi-label">Crédito</div><div class="kpi-value">{{ money(invSummarySplit.total_amount_credit) }}</div></div>
            <div class="kpi"><div class="kpi-label">Contado</div><div class="kpi-value">{{ money(invSummarySplit.total_amount_contado) }}</div></div>
            <div class="kpi"><div class="kpi-label">Saldo</div><div class="kpi-value">{{ money(invSummarySplit.total_balance) }}</div></div>
          </div>
          <div class="table-wrap">
            <table>
              <thead><tr><th>Identificador</th><th>Serie</th><th class="num">Número</th><th>Tipo</th><th>Fecha</th><th class="num">Monto</th><th class="num">Saldo</th><th>Cancelada</th></tr></thead>
              <tbody>
                <tr v-for="inv in (acc.invoices.results || [])" :key="inv.id">
                  <td>{{ inv.invoice_ident }}</td>
                  <td>{{ inv.invoice_series }}</td>
                  <td class="num">{{ inv.invoice_number }}</td>
                  <td>{{ invoiceTypeLabel(inv.invoice_type) }}</td>
                  <td>{{ fmtDT(inv.date_issued) }}</td>
                  <td class="num">{{ money(inv.amount) }}</td>
                  <td class="num">{{ money(inv.balance) }}</td>
                  <td>{{ inv.is_cancelled ? 'Sí' : 'No' }}</td>
                </tr>
                <tr v-if="!(acc.invoices.results || []).length"><td colspan="8" class="muted">Sin facturas</td></tr>
              </tbody>
            </table>
          </div>
        </StatCard>
        <StatCard :title="`${txTypeLabel} del día (${acc.payments.summary?.count || 0}) · ${money(acc.payments.summary?.total_amount)}`">
          <div class="table-wrap">
            <table>
              <thead><tr><th>Identificador</th><th>Referencia</th><th>Banco</th><th>Método</th><th>Fecha</th><th class="num">Monto</th></tr></thead>
              <tbody>
                <tr v-for="tx in (acc.payments.results || [])" :key="tx.id">
                  <td>{{ tx.transaction_ident }}</td>
                  <td>{{ tx.reference }}</td>
                  <td>{{ tx.bank }}</td>
                  <td>{{ tx.payment_method }}</td>
                  <td>{{ fmtDT(tx.transaction_date) }}</td>
                  <td class="num">{{ money(tx.amount) }}</td>
                </tr>
                <tr v-if="!(acc.payments.results || []).length"><td colspan="6" class="muted">Sin movimientos</td></tr>
              </tbody>
            </table>
          </div>
        </StatCard>
      </div>
    </section>


    <!-- UNIDADES TAB -->
<section v-else-if="activeTab==='maintenance'" :key="'maint-' + themeClass" class="tab-body">
  <div class="subtabs" role="tablist">
    <button
      v-for="s in maintSections"
      :key="s.key"
      role="tab"
      :aria-selected="s.key===maintSection"
      :class="['subtab', { active: s.key===maintSection }]"
      @click="maintSection = s.key"
    >{{ s.label }}</button>
  </div>

  <template v-if="maintSection==='graficas'">
  <div class="controls">
    <label>Fecha <input type="date" v-model="maintDate" @change="fetchMaintenance" /></label>
  </div>

  <div class="kpi-strip">
    <article v-for="k in maintKpis" :key="k.key" class="kpi-card">
      <div class="kpi-card__top"><span class="kpi-card__label">{{ k.label }}</span></div>
      <div class="kpi-card__value">{{ k.value }}</div>
      <div class="kpi-card__meta">
        <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
        <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
      </div>
    </article>
  </div>

  <div class="chart-grid">
    <StatCard title="Asignaciones por unidad">
      <p v-if="countUnitView.outliers.length" class="quality-flag">
        Valores atípicos, fuera de la escala:
        <span v-for="o in countUnitView.outliers" :key="o.name">{{ o.name }} ({{ fmtInt(o.value) }} OT)</span>
      </p>
      <v-chart :option="countPerUnitOption" autoresize :style="{ height: barHeight(countUnitView.plotted.length) }" />
      <details class="fold fold-inner">
        <summary>Detalle</summary>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Unidad</th><th class="num">OT</th></tr></thead>
            <tbody>
              <tr v-for="r in maint.perUnitCount" :key="r.unit_short_id">
                <td>{{ r.unit_short_id }}</td>
                <td class="num">{{ fmtInt(r.count) }}</td>
              </tr>
              <tr v-if="!maint.perUnitCount.length"><td colspan="2" class="muted">Sin datos</td></tr>
            </tbody>
          </table>
        </div>
      </details>
    </StatCard>

    <StatCard title="Monto por unidad">
      <p v-if="moneyUnitView.outliers.length" class="quality-flag">
        Valores atípicos, fuera de la escala:
        <span v-for="o in moneyUnitView.outliers" :key="o.name">{{ o.name }} ({{ money(o.value) }})</span>
      </p>
      <v-chart :option="moneyPerUnitOption" autoresize :style="{ height: barHeight(moneyUnitView.plotted.length) }" />
      <details class="fold fold-inner">
        <summary>Detalle</summary>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Unidad</th><th class="num">Monto</th></tr></thead>
            <tbody>
              <tr v-for="r in maint.perUnitMoney" :key="r.unit_short_id">
                <td>{{ r.unit_short_id }}</td>
                <td class="num">{{ money(r.amount) }}</td>
              </tr>
              <tr v-if="!maint.perUnitMoney.length"><td colspan="2" class="muted">Sin datos</td></tr>
            </tbody>
          </table>
        </div>
      </details>
    </StatCard>

    <StatCard title="Kilómetros por unidad">
      <p v-if="kmUnitView.outliers.length" class="quality-flag">
        Valores atípicos, fuera de la escala:
        <span v-for="o in kmUnitView.outliers" :key="o.name">{{ o.name }} ({{ fmtInt(o.value) }} km)</span>
      </p>
      <v-chart :option="kmPerUnitOption" autoresize :style="{ height: barHeight(kmUnitView.plotted.length) }" />
      <details class="fold fold-inner">
        <summary>Detalle</summary>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Unidad</th><th class="num">Kilómetros</th></tr></thead>
            <tbody>
              <tr v-for="r in maint.perUnitKm" :key="r.unit_short_id">
                <td>{{ r.unit_short_id }}</td>
                <td class="num">{{ fmtInt(r.km) }}</td>
              </tr>
              <tr v-if="!maint.perUnitKm.length"><td colspan="2" class="muted">Sin datos</td></tr>
            </tbody>
          </table>
        </div>
      </details>
    </StatCard>

    <StatCard title="Órdenes por tipo de unidad">
      <v-chart :option="otByPrefixOption" autoresize :style="{ height: barHeight(prefixBars.length) }" />
    </StatCard>
  </div>
  </template>

  <template v-else>
  <div class="controls">
    <label>Desde: <input type="date" v-model="unitStatsStart" @change="fetchUnitStats" /></label>
    <label>Hasta: <input type="date" v-model="unitStatsEnd" @change="fetchUnitStats" /></label>
    <label>
      Ordenar por:
      <select v-model="unitStatsSort" class="unit-sort">
        <option value="services">Servicios</option>
        <option value="money">Monto</option>
        <option value="km">Kilómetros</option>
        <option value="unit">Número de unidad</option>
      </select>
    </label>
    <label class="unit-toggle">
      <input type="checkbox" v-model="unitStatsOnlyActive" />
      Solo unidades con actividad
    </label>
    <label class="unit-toggle">
      <input type="checkbox" v-model="unitStatsOnlyIdle" />
      Solo unidades sin actividad
    </label>
    <span v-if="unitStatsLoading" class="muted">Cargando…</span>
  </div>

  <div class="kpi-strip">
    <article v-for="k in unitKpis" :key="k.key" class="kpi-card">
      <div class="kpi-card__top"><span class="kpi-card__label">{{ k.label }}</span></div>
      <div class="kpi-card__value">{{ k.value }}</div>
      <div class="kpi-card__meta">
        <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
        <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
      </div>
    </article>
  </div>

  <div class="unit-pills" role="tablist">
    <button
      v-for="c in UNIT_CATEGORY_COLUMNS"
      :key="c.key"
      role="tab"
      :aria-selected="c.key===unitStatsCategory"
      :class="['unit-pill', `unit-pill--${c.key}`, { active: c.key===unitStatsCategory }]"
      @click="unitStatsCategory = c.key"
    >
      <span class="unit-pill__dot"></span>
      {{ c.title }}
      <span class="unit-pill__count">{{ unitStatsCounts[c.key] }}</span>
    </button>
  </div>

  <div class="unit-grid" :class="`unit-grid--${unitStatsCategory}`">
    <article v-for="u in unitStatsVisible" :key="u.id" class="unit-stat-card">
      <div class="unit-stat-card__top">
        <span class="unit-stat-card__id">{{ u.unit_short_id }}</span>
        <span v-if="u.unit_category" class="unit-stat-card__cat">{{ u.unit_category }}</span>
      </div>
      <div class="unit-stat-card__metrics">
        <div class="unit-metric">
          <span class="unit-metric__label">Servicios</span>
          <span class="unit-metric__value">{{ fmtInt(u.services) }}</span>
        </div>
        <div class="unit-metric">
          <span class="unit-metric__label">Monto</span>
          <span class="unit-metric__value">{{ fmtMoney0(u.money) }}</span>
        </div>
        <div class="unit-metric">
          <span class="unit-metric__label">KM</span>
          <span class="unit-metric__value">{{ fmtInt(u.km) }}</span>
        </div>
      </div>
      <div class="unit-stat-card__last" :class="{ 'unit-stat-card__last--alert': isUnitStale(u) }">
        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm4.2 14.2L11 13.4V6h1.8v6.4l4.4 2.6Z" fill="currentColor"/></svg>
        <span v-if="u.last_service_at">
          Último servicio {{ elapsedLabel(u.last_service_at) }} · {{ fmtDateTime(u.last_service_at) }}
        </span>
        <span v-else>Sin servicios registrados</span>
      </div>
    </article>

    <div v-if="!unitStatsVisible.length" class="unit-grid__empty">Sin unidades</div>
  </div>
  </template>
</section>
    <!-- RRHH TAB -->
<section v-else-if="activeTab==='rrhh'" :key="'rrhh-' + themeClass" class="tab-body">
  <div class="controls">
    <label>Fecha <input type="date" v-model="rrhhDate" @change="fetchRRHH" /></label>
    <label>Mes <input type="month" v-model="rrhhMonthYear" @change="fetchRRHHMonthly" /></label>
  </div>

  <div class="kpi-strip">
    <article v-for="k in rrhhKpis" :key="k.key" class="kpi-card">
      <div class="kpi-card__top"><span class="kpi-card__label">{{ k.label }}</span></div>
      <div class="kpi-card__value">{{ k.value }}</div>
      <div class="kpi-card__meta">
        <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
        <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
      </div>
      <v-chart v-if="k.spark" class="kpi-spark" :option="k.spark" autoresize />
    </article>
  </div>

  <section class="capture-board">
    <h2>Captura del mes</h2>
    <div class="rrhh-matrix-wrap">
      <table class="rrhh-matrix-table">
        <thead>
          <tr>
            <th class="sticky-col">Capturista</th>
            <th v-for="day in rrhhMonthlyDays" :key="`h-day-${day}`" class="num">{{ day }}</th>
            <th class="num">Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in rrhhShiftGroups" :key="group.key">
            <tr v-if="group.label" class="shift-head">
              <td :colspan="rrhhMonthlyDays.length + 2">{{ group.label }}</td>
            </tr>
            <tr v-for="row in group.rows" :key="`m-${row.clerk_id}`">
              <td class="sticky-col">{{ row.clerk_name }}</td>
              <td
                v-for="(val, idx) in (row.daily || [])"
                :key="`r-${row.clerk_id}-d-${idx}`"
                class="num day-cell"
                :style="isFutureDay(rrhhMonthlyDays[idx], rrhhMonthYear) ? null : rrhhMatrixCellStyle(val)"
              >
                {{ isFutureDay(rrhhMonthlyDays[idx], rrhhMonthYear) ? '—' : fmtInt(val) }}
              </td>
              <td class="num total-cell">{{ fmtInt(row.total) }}</td>
            </tr>
          </template>
          <tr v-if="!rrhhMonthlyTotalsSorted.length">
            <td :colspan="rrhhMonthlyDays.length + 2" class="muted">Sin datos</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="att-board">
    <div class="att-board__bar">
      <h2>Asistencia</h2>
      <span v-if="attendanceLoading" class="muted">Cargando…</span>
      <span v-else-if="attendanceError" class="quality-flag">{{ attendanceError }}</span>
    </div>
    <div v-if="attendanceTabs.length" class="att-tabs" role="tablist">
      <button
        v-for="tab in attendanceTabs"
        :key="tab.key"
        type="button"
        class="att-tab"
        :class="{ active: tab.key === attendanceGroup }"
        @click="attendanceGroup = tab.key"
      >{{ tab.label }} ({{ tab.count }})</button>
    </div>
    <div v-if="attendanceRows.length" class="att-wrap">
      <table class="att-table">
        <thead>
          <tr>
            <th class="sticky-col">Empleado</th>
            <th v-for="(label, idx) in attendanceHeaders" :key="`ah-${idx}`" class="num">{{ label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in attendanceRows" :key="row.employee_id">
            <td class="sticky-col">{{ row.employee_ident || '—' }} — {{ row.employee_name }}</td>
            <td v-for="(code, idx) in row.daily" :key="`ac-${row.employee_id}-${idx}`" class="num">
              <span class="att-pill" :class="attendanceStatusClass(code)">{{ code || '-' }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else-if="!attendanceLoading" class="muted">No hay personal seleccionado o no hay datos en este mes.</p>
    <div class="att-legend">
      <span v-for="opt in attendanceLegend" :key="opt.code" class="att-legend__item">
        <span class="att-pill" :class="attendanceStatusClass(opt.code)">{{ opt.code }}</span>
        {{ opt.label }}
      </span>
    </div>
  </section>
</section>
<section v-else-if="activeTab==='estadistico'" :key="'est-' + themeClass" class="tab-body">
  <div class="controls">
    <label>
      Mes:
      <input type="month" v-model="estadisticoMonth" @change="fetchEstadisticoClientes" />
    </label>
  </div>

  <div class="kpi-strip">
    <article v-for="k in estadisticoKpis" :key="k.key" class="kpi-card">
      <div class="kpi-card__top"><span class="kpi-card__label">{{ k.label }}</span></div>
      <div class="kpi-card__value">{{ k.value }}</div>
      <div class="kpi-card__meta">
        <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
        <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
      </div>
      <v-chart v-if="k.spark" class="kpi-spark" :option="k.spark" autoresize />
    </article>
  </div>

  <div class="estadistico-stack">
    <StatCard :title="`Servicios por cliente (${estadisticoYear})`">
      <div class="estadistico-table-wrap">
        <table class="estadistico-table">
          <thead>
            <tr>
              <th class="sticky-col">Cliente</th>
              <th v-for="label in estadisticoMonthLabels" :key="`y-count-h-${label}`" class="num">{{ label }}</th>
              <th class="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in estadisticoYearly.countRows" :key="`y-count-r-${row.client}`">
              <td class="sticky-col">{{ row.client }}</td>
              <td v-for="(v, idx) in row.monthly" :key="`y-count-v-${row.client}-${idx}`" class="num">{{ yearCell(v, idx, false) }}</td>
              <td class="num total-cell">{{ row.total }}</td>
            </tr>
            <tr v-if="estadisticoYearly.countRows.length">
              <td class="sticky-col total-label">Total</td>
              <td v-for="(v, idx) in estadisticoYearly.countTotalMonthly" :key="`y-count-total-m-${idx}`" class="num total-cell">{{ yearCell(v, idx, false) }}</td>
              <td class="num total-cell">{{ estadisticoYearly.countGrandTotal }}</td>
            </tr>
            <tr v-if="!estadisticoYearly.countRows.length">
              <td :colspan="estadisticoMonthLabels.length + 2" class="muted">Sin datos</td>
            </tr>
          </tbody>
        </table>
      </div>
    </StatCard>

    <StatCard :title="`Ingresos por cliente (${estadisticoYear})`">
      <div class="estadistico-table-wrap">
        <table class="estadistico-table">
          <thead>
            <tr>
              <th class="sticky-col">Cliente</th>
              <th v-for="label in estadisticoMonthLabels" :key="`y-subtotal-h-${label}`" class="num">{{ label }}</th>
              <th class="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in estadisticoYearly.subtotalRows" :key="`y-subtotal-r-${row.client}`">
              <td class="sticky-col">{{ row.client }}</td>
              <td v-for="(v, idx) in row.monthly" :key="`y-subtotal-v-${row.client}-${idx}`" class="num">{{ yearCell(v, idx, true) }}</td>
              <td class="num total-cell">{{ money(row.total) }}</td>
            </tr>
            <tr v-if="estadisticoYearly.subtotalRows.length">
              <td class="sticky-col total-label">Total</td>
              <td v-for="(v, idx) in estadisticoYearly.subtotalTotalMonthly" :key="`y-subtotal-total-m-${idx}`" class="num total-cell">{{ yearCell(v, idx, true) }}</td>
              <td class="num total-cell">{{ money(estadisticoYearly.subtotalGrandTotal) }}</td>
            </tr>
            <tr v-if="!estadisticoYearly.subtotalRows.length">
              <td :colspan="estadisticoMonthLabels.length + 2" class="muted">Sin datos</td>
            </tr>
          </tbody>
        </table>
      </div>
    </StatCard>

    <details class="fold" open>
      <summary>Servicios por cliente, día a día</summary>
    <StatCard title="Servicios del mes">
      <div class="estadistico-table-wrap">
        <table class="estadistico-table">
          <thead>
            <tr>
              <th class="sticky-col">Cliente</th>
              <th v-for="day in estadistico.days" :key="`count-h-${day}`" class="num">{{ day }}</th>
              <th class="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in estadistico.countRows" :key="`count-r-${row.client}`">
              <td class="sticky-col">{{ row.client }}</td>
              <td v-for="(v, idx) in row.daily" :key="`count-v-${row.client}-${idx}`" class="num">{{ dayCell(v, estadistico.days[idx], false) }}</td>
              <td class="num total-cell">{{ row.total }}</td>
            </tr>
            <tr v-if="estadistico.countRows.length">
              <td class="sticky-col total-label">Total</td>
              <td v-for="(v, idx) in estadistico.countTotalDaily" :key="`count-total-d-${idx}`" class="num total-cell">{{ dayCell(v, estadistico.days[idx], false) }}</td>
              <td class="num total-cell">{{ estadistico.countGrandTotal }}</td>
            </tr>
            <tr v-if="!estadistico.countRows.length">
              <td :colspan="estadistico.days.length + 2" class="muted">Sin datos</td>
            </tr>
          </tbody>
        </table>
      </div>
    </StatCard>
    </details>

    <details class="fold" open>
      <summary>Ingresos por cliente, día a día</summary>
    <StatCard title="Ingresos del mes">
      <div class="estadistico-table-wrap">
        <table class="estadistico-table">
          <thead>
            <tr>
              <th class="sticky-col">Cliente</th>
              <th v-for="day in estadistico.days" :key="`subtotal-h-${day}`" class="num">{{ day }}</th>
              <th class="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in estadistico.subtotalRows" :key="`subtotal-r-${row.client}`">
              <td class="sticky-col">{{ row.client }}</td>
              <td v-for="(v, idx) in row.daily" :key="`subtotal-v-${row.client}-${idx}`" class="num">{{ dayCell(v, estadistico.days[idx], true) }}</td>
              <td class="num total-cell">{{ money(row.total) }}</td>
            </tr>
            <tr v-if="estadistico.subtotalRows.length">
              <td class="sticky-col total-label">Total</td>
              <td v-for="(v, idx) in estadistico.subtotalTotalDaily" :key="`subtotal-total-d-${idx}`" class="num total-cell">{{ dayCell(v, estadistico.days[idx], true) }}</td>
              <td class="num total-cell">{{ money(estadistico.subtotalGrandTotal) }}</td>
            </tr>
            <tr v-if="!estadistico.subtotalRows.length">
              <td :colspan="estadistico.days.length + 2" class="muted">Sin datos</td>
            </tr>
          </tbody>
        </table>
      </div>
    </StatCard>
    </details>
  </div>
</section>

<!-- PUBLICIDAD (Google Ads) TAB -->
<section v-else-if="activeTab==='publicidad'" :key="'ads-' + themeClass" class="tab-body">
  <div class="controls ads-controls">
    <label>Desde: <input type="date" v-model="adsStart" :max="adsEnd" @change="onAdsRangeChange" /></label>
    <label>Hasta: <input type="date" v-model="adsEnd" :min="adsStart" @change="onAdsRangeChange" /></label>
    <div class="ads-presets">
      <button type="button" :class="['ads-preset', { active: adsPreset==='7d' }]" @click="setAdsPreset('7d')">Últimos 7 días</button>
      <button type="button" :class="['ads-preset', { active: adsPreset==='30d' }]" @click="setAdsPreset('30d')">Últimos 30 días</button>
      <button type="button" :class="['ads-preset', { active: adsPreset==='month' }]" @click="setAdsPreset('month')">Este mes</button>
    </div>
  </div>

  <div class="kpi-strip">
    <article v-for="k in adsKpis" :key="k.key" class="kpi-card">
      <div class="kpi-card__top"><span class="kpi-card__label">{{ k.label }}</span></div>
      <div class="kpi-card__value">{{ k.value }}</div>
      <div class="kpi-card__meta">
        <span class="kpi-delta" :class="k.delta && k.delta.cls">{{ k.delta.text }}</span>
        <span class="kpi-hint" :class="k.delta && k.delta.cls">{{ k.hint }}</span>
      </div>
      <v-chart v-if="k.spark" class="kpi-spark" :option="k.spark" autoresize />
    </article>
  </div>

  <div class="ads-group">
    <h3 class="ads-group-title"><span class="ads-group-bar"></span>Remolque</h3>
    <AdsCampaignCard
      v-for="c in adsCampaigns.remolque"
      :key="c.name"
      :title="c.title"
      :data="ads[c.name] ? ads[c.name].data : null"
      :loading="ads[c.name] ? ads[c.name].loading : true"
    />
    <StatCard class="ads-internal-card" title="Sistema · Servicios cerrados y Ganancia">
      <MetricSeriesBlock
        :metrics="adsInternalMetrics"
        :data="adsInternal.remolque ? adsInternal.remolque.data : null"
        :loading="adsInternal.remolque ? adsInternal.remolque.loading : true"
        :default-selected="['services_closed', 'ganancia']"
      />
    </StatCard>
  </div>

  <div class="ads-group">
    <h3 class="ads-group-title"><span class="ads-group-bar montacargas"></span>Montacargas</h3>
    <AdsCampaignCard
      v-for="c in adsCampaigns.montacargas"
      :key="c.name"
      :title="c.title"
      :data="ads[c.name] ? ads[c.name].data : null"
      :loading="ads[c.name] ? ads[c.name].loading : true"
    />
    <StatCard class="ads-internal-card" title="Sistema · Servicios cerrados y Ganancia">
      <MetricSeriesBlock
        :metrics="adsInternalMetrics"
        :data="adsInternal.montacargas ? adsInternal.montacargas.data : null"
        :loading="adsInternal.montacargas ? adsInternal.montacargas.loading : true"
        :default-selected="['services_closed', 'ganancia']"
      />
    </StatCard>
  </div>

  <StatCard
    class="ads-services-card"
    :title="`Servicios que califican (${adsServices.data ? adsServices.data.count || 0 : 0})`"
  >
    <div class="table-wrap ads-services-table scroll-rows-15">
      <table>
        <thead>
          <tr>
            <th>Cliente</th>
            <th class="num"># Nota</th>
            <th>Tipo</th>
            <th class="num">Total</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in (adsServices.data ? adsServices.data.results : [])" :key="row.note_ident || row.note_number">
            <td>{{ row.client_name }}</td>
            <td class="num">{{ row.note_number }}</td>
            <td>
              <span class="ads-type-chip" :class="row.category === 'Montacargas' ? 'mont' : 'remo'">{{ row.category }}</span>
            </td>
            <td class="num">{{ money(row.total) }}</td>
            <td class="num">
              <a
                v-if="row.note_ident"
                class="ads-open-btn"
                :href="adsNoteBaseUrl + row.note_ident"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
              </a>
              <span v-else class="muted">—</span>
            </td>
          </tr>
          <tr v-if="adsServices.loading">
            <td colspan="5" class="muted">Cargando…</td>
          </tr>
          <tr v-else-if="!adsServices.data || !adsServices.data.results || !adsServices.data.results.length">
            <td colspan="5" class="muted">Sin servicios que califiquen en el periodo.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </StatCard>
</section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, provide } from 'vue'
import axios from 'axios'
import { THEME_KEY } from 'vue-echarts'
import StatCard from './primitives/StatCard.vue'
import AdsCampaignCard from './AdsCampaignCard.vue'
import MetricSeriesBlock from './MetricSeriesBlock.vue'
import { useDashboardWS } from '../composables/useDashboardWS'

const tabs = [
  { key: 'operations', label: 'Operaciones' },
  { key: 'accounting', label: 'Contabilidad' },
  { key: 'maintenance', label: 'Unidades' },
  { key: 'rrhh', label: 'RRHH' },
  { key: 'estadistico', label: 'Estadístico' },
  { key: 'publicidad', label: 'Publicidad' },
]
const activeTab = ref('operations')
const compareMode = ref('none')

const currentYear = new Date().getFullYear()
const opsYear = ref(currentYear)

const todayMonterrey = new Date().toLocaleString("en-US", { timeZone: "America/Monterrey" });
const local = new Date(todayMonterrey);
const todayISO = local.toLocaleDateString("en-CA").slice(0, 10);
const opsDate = ref(todayISO) 
const accDate = ref(todayISO)
const txType = ref(2)

const opsMonth = ref((opsDate.value || '').slice(0, 7) || new Date().toISOString().slice(0,7))

const ops = reactive({
  ordersPerClient: [],
  activeUnits: [],
  typeFrequency: [],
  sucursalCount: [],
  sucursalSubtotal: [],
  dailyCount: [],        // NEW
  dailySubtotal: [],      // NEW
  monthlyCount: [],      // NEW: [{month, count}]
  monthlySubtotal: [],
  insurerDaily: [],      // AXA: [{date, total, accepted, timed_out, errored, ...}]
  insurerTotals: {}      // AXA month totals
})

// add to acc state
const acc = reactive({
  invoices: { summary: {}, results: [] },
  payments: { summary: {}, results: [] },
  uninvoiced: 0,
  balPerClient: [],       // NEW
  totalCredit: 0,
  overdue: 0,
})

const opsPrev = reactive({ orders: null, clients: null, subtotal: null })
const opsPrevMonth = reactive({ dailyCount: [], dailySubtotal: [] })
const opsYearCompare = reactive({ dailyCount: [], dailySubtotal: [], insurerDaily: [], monthlyCount: [], monthlySubtotal: [] })
const accPrev = reactive({ invoices: null, payments: null })
const accAging = reactive({ al_dia: 0, d7: 0, d14: 0, d21: 0, over_21: 0 })
const accCompare = reactive({ credit: [], uninvoiced: [] })
const maintPrev = reactive({ count: null, money: null, km: null, units: null })
const unitStatsPrev = reactive({ units: null, services: null, money: null, km: null })
const rrhhPrev = reactive({ today: null, month: null })
const estadisticoPrev = reactive({ count: null, subtotal: null })
const adsPrev = reactive({ cost: null, clicks: null, services: null, ganancia: null })

// RRHH state
const rrhhDate = ref(opsDate.value)     // reuse Monterrey-local date you already compute
const rrhh = reactive({ perClerk: [] })
const rrhhMonthYear = ref(
  `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2,"0")}`
)
const rrhhMonthlyDays   = ref([])                    // [1,2,...]
const rrhhMonthlySeries = ref([])                    // [{clerk_id, clerk_name, daily[], total}]
const CAPTURE_SHIFTS = [
  { key: 'day', label: 'Día' },
  { key: 'afternoon', label: 'Tarde' },
  { key: 'night', label: 'Noche' },
  { key: 'extended', label: 'Extendido' },
]
const captureConfig = reactive({ configured: false, ids: [], shifts: {} })

function captureShift(id) {
  const raw = String((captureConfig.shifts || {})[String(id)] || '').trim().toLowerCase()
  const shift = raw === 'tarde' ? 'afternoon' : raw
  return CAPTURE_SHIFTS.some(s => s.key === shift) ? shift : ''
}
function inCaptureRoster(id) {
  if (!captureConfig.configured) return true
  const ids = new Set((captureConfig.ids || []).map(Number))
  return ids.has(Number(id)) && !!captureShift(id)
}
const estadisticoMonth = ref(rrhhMonthYear.value)
const estadisticoMonthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const estadisticoYear = computed(() => String(estadisticoMonth.value || '').slice(0, 4))
const estadistico = reactive({
  days: [],
  countRows: [],
  countTotalDaily: [],
  countGrandTotal: 0,
  subtotalRows: [],
  subtotalTotalDaily: [],
  subtotalGrandTotal: 0,
})
const estadisticoYearly = reactive({
  loadedYear: null,
  countRows: [],
  countTotalMonthly: [],
  countGrandTotal: 0,
  subtotalRows: [],
  subtotalTotalMonthly: [],
  subtotalGrandTotal: 0,
})

function apiBase() {
  const API = process.env.API_DIRECTORY || '';
  return `${API}`;
}

// Identity colors for clients and branches only. Stable per name, and kept
// off the green / amber / red reserved for status.
const ENTITY_PALETTE = [
  '#1d4ed8', '#0369a1', '#4338ca', '#0f766e', '#1e3a8a',
  '#075985', '#5b21b6', '#155e75', '#312e81', '#334155',
]
const OTROS_COLOR = '#94a3b8'
const COMPARE_COLOR = '#94a3b8'

const PREFIX_NAMES = {
  '210': 'Montacargas',
  '230': 'Grúas Tipo A',
  '231': 'Grúas Tipo B',
  '233': 'Grúas Tipo C y D',
  '241': 'Tractor',
  '280': 'Grupo 280',
  '643': 'Auxilio vial',
}

const TX_TYPES = [
  { id: 2, label: 'Pagos' },
  { id: 3, label: 'Pago parcial' },
  { id: 1, label: 'Facturación' },
  { id: 6, label: 'Bonificaciones' },
  { id: 7, label: 'Factoraje' },
  { id: 5, label: 'Ajuste de saldo' },
  { id: 4, label: 'Transferencia a incobrable' },
  { id: 8, label: 'Cancelación de factura' },
]

const TOP_N = 10

function entityColor(name) {
  const s = String(name || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return ENTITY_PALETTE[h % ENTITY_PALETTE.length]
}
function prefixLabel(code) {
  const key = String(code || '')
  return PREFIX_NAMES[key] || (key ? `Grupo ${key}` : 'Sin grupo')
}

function areaGradient(hex) {
  return {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: hex + '59' },
      { offset: 1, color: hex + '03' },
    ],
  }
}

const fmtInt    = (v) => (Number(v) || 0).toLocaleString('es-MX')
const fmtMoney0 = (v) => (Number(v) || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })

function money(v){ const n = Number(v || 0); return n.toLocaleString('es-MX', { style:'currency', currency:'MXN' }) }

function fmtDate(v) {
  if (!v) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v).slice(0, 10))
  if (!m) return String(v)
  return `${m[3]}/${m[2]}/${m[1]}`
}
function fmtDT(v) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return fmtDate(v)
  const date = d.toLocaleDateString('es-MX', {
    timeZone: 'America/Monterrey', day: '2-digit', month: '2-digit', year: 'numeric',
  })
  const time = d.toLocaleTimeString('es-MX', {
    timeZone: 'America/Monterrey', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  return `${date} ${time}`
}

function shiftISO(iso, days) {
  const [y, m, d] = String(iso || todayISO).split('-').map(Number)
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}
function shiftMonth(ym, delta) {
  const [y, m] = String(ym || todayISO.slice(0, 7)).split('-').map(Number)
  const dt = new Date(Date.UTC(y, (m || 1) - 1 + delta, 1))
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`
}
function periodEnd(monthYm) {
  if (!monthYm) return null
  const [y, m] = String(monthYm).split('-').map(Number)
  const todayY = Number(todayISO.slice(0, 4))
  const todayM = Number(todayISO.slice(5, 7))
  if (y > todayY || (y === todayY && m > todayM)) return null
  if (y === todayY && m === todayM) return todayISO
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return `${monthYm}-${String(last).padStart(2, '0')}`
}
function clipRows(rows, monthYm) {
  const end = periodEnd(monthYm)
  if (!end) return []
  return (rows || []).filter(r => r.date && r.date <= end)
}
function clipYear(values, year) {
  const arr = (values || []).map(v => Number(v) || 0)
  const y = Number(year)
  const nowY = Number(todayISO.slice(0, 4))
  const nowM = Number(todayISO.slice(5, 7))
  if (!y || y > nowY) return []
  if (y < nowY) return arr
  return arr.slice(0, nowM)
}
function isFutureMonthIndex(idx, year) {
  const y = Number(year)
  const nowY = Number(todayISO.slice(0, 4))
  const nowM = Number(todayISO.slice(5, 7))
  if (y > nowY) return true
  if (y < nowY) return false
  return idx >= nowM
}
function isFutureDay(dayNum, monthYm) {
  const end = periodEnd(monthYm)
  if (!end) return true
  const iso = `${monthYm}-${String(dayNum).padStart(2, '0')}`
  return iso > end
}
function elapsedDayCount(monthYm) {
  const end = periodEnd(monthYm)
  if (!end) return 0
  return Number(end.slice(8, 10))
}
function sumFirstDays(rows, n, key) {
  return (rows || []).slice(0, n).reduce((s, r) => s + Number(r[key] || 0), 0)
}
function outlierMask(values) {
  const nums = values.map(v => Number(v) || 0)
  if (nums.length < 3) return nums.map(() => false)
  const positive = nums.filter(n => n > 0).sort((a, b) => a - b)
  const median = positive.length ? positive[Math.floor(positive.length / 2)] : 0
  const mean = nums.reduce((s, n) => s + n, 0) / nums.length
  const sd = Math.sqrt(nums.reduce((s, n) => s + (n - mean) ** 2, 0) / nums.length)
  return nums.map(n => {
    if (n <= 0) return false
    const bySpread = nums.length >= 4 && sd > 0 && n > mean + 3 * sd
    const byRatio = median > 0 && n > median * 8
    return bySpread || byRatio
  })
}
function periodDelta(current, previous) {
  if (previous == null || Number.isNaN(Number(previous))) return { text: 'Sin comparación', cls: 'flat' }
  const c = Number(current) || 0
  const p = Number(previous) || 0
  if (p === 0 && c === 0) return { text: '0%', cls: 'flat' }
  if (p === 0) return { text: '▲ nuevo', cls: 'up' }
  const pct = ((c - p) / Math.abs(p)) * 100
  if (pct > 0.5) return { text: `▲ ${Math.abs(pct).toFixed(0)}%`, cls: 'up' }
  if (pct < -0.5) return { text: `▼ ${Math.abs(pct).toFixed(0)}%`, cls: 'down' }
  return { text: '● 0%', cls: 'flat' }
}
function topWithOthers(rows, nameOf, valueOf, n = TOP_N) {
  const items = (rows || []).map(r => ({
    name: nameOf(r) || '—',
    value: Number(valueOf(r) || 0),
    overdue: Number(r.overdue || 0),
  })).sort((a, b) => b.value - a.value)
  const top = items.slice(0, n)
  const rest = items.slice(n)
  const others = rest.reduce((s, r) => s + r.value, 0)
  if (others > 0) top.push({ name: 'Otros', value: others, overdue: 0 })
  return top
}
function splitScale(items) {
  const mask = outlierMask(items.map(i => i.value))
  const normal = []
  const outliers = []
  items.forEach((item, i) => {
    if (mask[i]) outliers.push(item)
    else normal.push(item)
  })
  return { normal, outliers }
}

/* ───────────────────────── Theme (light / dark) ───────────────────────── */
const isDark = ref(true)
try {
  const saved = localStorage.getItem('masterDashTheme')
  if (saved) isDark.value = saved === 'dark'
} catch (e) { /* ignore */ }

const themeClass = computed(() => (isDark.value ? 'theme-dark' : 'theme-light'))

function toggleTheme() {
  isDark.value = !isDark.value
  try { localStorage.setItem('masterDashTheme', isDark.value ? 'dark' : 'light') } catch (e) { /* ignore */ }
}

const chartFont = 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif'

// Visual-only ECharts theme. Series/data/logic are untouched; any explicit
// colors set inside each chart option still take precedence over this.
function buildChartTheme(dark) {
  const axisLabel = dark ? '#94a3b8' : '#475569'
  const axisLine  = dark ? 'rgba(148,163,184,0.30)' : 'rgba(15,23,42,0.18)'
  const splitLine = dark ? 'rgba(148,163,184,0.10)' : 'rgba(15,23,42,0.07)'
  const axis = {
    axisLine: { lineStyle: { color: axisLine } },
    axisTick: { lineStyle: { color: axisLine } },
    axisLabel: { color: axisLabel },
    splitLine: { lineStyle: { color: splitLine } },
  }
  return {
    color: [isDark.value ? '#ffc434' : '#9a7209'],
    backgroundColor: 'transparent',
    textStyle: { color: dark ? '#cbd5e1' : '#334155', fontFamily: chartFont },
    title: { textStyle: { color: dark ? '#e2e8f0' : '#0f172a' } },
    categoryAxis: axis,
    valueAxis: axis,
    legend: { textStyle: { color: dark ? '#cbd5e1' : '#334155' } },
    tooltip: {
      backgroundColor: dark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
      borderColor: dark ? 'rgba(148,163,184,0.25)' : 'rgba(15,23,42,0.12)',
      borderWidth: 1,
      textStyle: { color: dark ? '#e2e8f0' : '#0f172a' },
      extraCssText: dark
        ? 'box-shadow:0 10px 30px rgba(0,0,0,0.5);border-radius:12px;'
        : 'box-shadow:0 10px 30px rgba(15,23,42,0.14);border-radius:12px;',
    },
  }
}
provide(THEME_KEY, computed(() => buildChartTheme(isDark.value)))

/* ───────────────────────── Live clock (Monterrey) ───────────────────────── */
const clock = reactive({ time: '--:--:--', date: '' })
let clockTimer = null
function tickClock() {
  const now = new Date()
  clock.time = now.toLocaleTimeString('es-MX', {
    timeZone: 'America/Monterrey', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  clock.date = now.toLocaleDateString('es-MX', {
    timeZone: 'America/Monterrey', weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
  })
}
onMounted(() => { tickClock(); clockTimer = setInterval(tickClock, 1000) })
onUnmounted(() => { if (clockTimer) clearInterval(clockTimer) })

/* ─────────────── Derived KPIs for hero strips (no new fetches) ─────────────── */
const opsTotalOrders  = computed(() => (ops.ordersPerClient || []).reduce((s, r) => s + Number(r.orders || 0), 0))
const maintTotalMoney = computed(() => (maint.perUnitMoney || []).reduce((s, r) => s + Number(r.amount || 0), 0))
const maintTotalKm    = computed(() => (maint.perUnitKm || []).reduce((s, r) => s + Number(r.km || 0), 0))
const rrhhTotalToday  = computed(() => (rrhh.perClerk || []).filter(r => inCaptureRoster(r.clerk_id)).reduce((s, r) => s + Number(r.count || 0), 0))
const rrhhMonthTotal  = computed(() => (rrhhMonthlySeries.value || []).filter(r => inCaptureRoster(r.clerk_id)).reduce((s, r) => s + Number(r.total || 0), 0))

const pendingBillingByClient = ref([])
const pendingBillingCount = ref(0)
const pendingBillingTotal = ref(0)

async function fetchPendingBillingByClient() {
  const base = apiBase()
  const r = await axios.get(`${base}/dashboard/pending-billing-subtotal-by-client`, {
    params: { limit: 10 }
  })
  pendingBillingByClient.value = r.data.results || []
  pendingBillingCount.value = Number(r.data.count || 0)
  pendingBillingTotal.value = Number(r.data.total || 0)
}

async function fetchOperations(){
  const base = apiBase()
  const prev = shiftISO(opsDate.value, -1)
  const [c1, c3, c4, c5, p1, p5] = await Promise.all([
    axios.get(`${base}/dashboard/orders-per-client`, { params: { date: opsDate.value, limit: 1000 } }),
    axios.get(`${base}/dashboard/type-frequency`, { params: { date: opsDate.value } }),
    axios.get(`${base}/dashboard/orders-per-sucursal-count`, { params: { date: opsDate.value } }),
    axios.get(`${base}/dashboard/subtotal-per-sucursal-sum`, { params: { date: opsDate.value } }),
    axios.get(`${base}/dashboard/orders-per-client`, { params: { date: prev, limit: 1000 } }),
    axios.get(`${base}/dashboard/subtotal-per-sucursal-sum`, { params: { date: prev } }),
  ])
  ops.ordersPerClient   = c1.data.results || []
  ops.typeFrequency     = c3.data.results || []
  ops.sucursalCount     = c4.data.results || []
  ops.sucursalSubtotal  = c5.data.results || []
  const prevRows = p1.data.results || []
  opsPrev.orders = prevRows.reduce((s, r) => s + Number(r.orders || 0), 0)
  opsPrev.clients = prevRows.length
  opsPrev.subtotal = (p5.data.results || []).reduce((s, r) => s + Number(r.amount || 0), 0)
}

const fleetBoard = ref('gruas')
const fleetUnits = ref([])
const fleetVisibleIds = ref(null)
const fleetLoading = ref(false)
const fleetError = ref('')

function isFleetMontacargas(unit) {
  return String(unit.unit_category || '').toUpperCase() === 'MONTACARGAS'
}
function fleetIsVisible(unit) {
  return fleetVisibleIds.value === null || fleetVisibleIds.value.has(unit.id)
}
const fleetCounts = computed(() => {
  let gruas = 0
  let montacargas = 0
  for (const unit of fleetUnits.value) {
    if (!fleetIsVisible(unit)) continue
    if (isFleetMontacargas(unit)) montacargas += 1
    else gruas += 1
  }
  return { gruas, montacargas }
})
function fleetSort(list, key) {
  const byUnit = (a, b) => String(a.unit_short_id).localeCompare(String(b.unit_short_id), undefined, { numeric: true })
  if (key !== 'libre' && key !== 'fuera') return [...list].sort(byUnit)
  return [...list].sort((a, b) => {
    const ha = hoursSinceNow(a.last_service_at)
    const hb = hoursSinceNow(b.last_service_at)
    if (ha === null && hb === null) return byUnit(a, b)
    if (ha === null) return 1
    if (hb === null) return -1
    return hb - ha
  })
}
const fleetColumns = computed(() => {
  const wantMontacargas = fleetBoard.value === 'montacargas'
  const byState = { libre: [], en_servicio: [], fuera: [], mantenimiento: [] }
  for (const unit of fleetUnits.value) {
    if (!fleetIsVisible(unit) || !byState[unit.state]) continue
    if (isFleetMontacargas(unit) !== wantMontacargas) continue
    byState[unit.state].push(unit)
  }
  return [
    { key: 'libre', title: 'Libre', units: fleetSort(byState.libre, 'libre') },
    { key: 'en_servicio', title: 'En servicio', units: fleetSort(byState.en_servicio, 'en_servicio') },
    { key: 'fuera', title: 'Fuera de servicio', units: fleetSort(byState.fuera, 'fuera') },
    { key: 'mantenimiento', title: 'Mantenimiento', units: fleetSort(byState.mantenimiento, 'mantenimiento') },
  ]
})
function fleetCardAlert(key, unit) {
  if (key === 'mantenimiento') {
    const hours = hoursSinceNow(unit.maintenance_since)
    return hours !== null && hours >= UNIT_STALE_HOURS
  }
  if (key === 'libre' || key === 'fuera') return isUnitStale(unit)
  return false
}
async function fetchFleet() {
  fleetLoading.value = true
  fleetError.value = ''
  try {
    const [overview, config] = await Promise.all([
      axios.get(`${apiBase()}/dashboard/units-overview`),
      axios.get(`${apiBase()}/dashboard/units-overview-config`).catch(() => null),
    ])
    fleetUnits.value = overview.data?.results || []
    const data = config && config.data
    fleetVisibleIds.value = data && data.is_configured ? new Set(data.visible_unit_ids || []) : null
  } catch (e) {
    fleetError.value = 'No se pudo cargar la flota.'
  } finally {
    fleetLoading.value = false
  }
}

async function fetchAccounting(){
  const base = apiBase()
  const prev = shiftISO(accDate.value, -1)
  const [c1, c2, c3, c4, c5, aging, p1, p2] = await Promise.all([
    axios.get(`${base}/dashboard/invoices-today`, { params: { date: accDate.value } }),
    axios.get(`${base}/dashboard/payments-today`, { params: { date: accDate.value, transaction_type: txType.value } }),
    axios.get(`${base}/dashboard/uninvoiced-orders-count`),
    axios.get(`${base}/dashboard/balance-per-client`),
    axios.get(`${base}/dashboard/total-credit-balance`),
    axios.get(`${base}/dashboard/credit-aging-bins`),
    axios.get(`${base}/dashboard/invoices-today`, { params: { date: prev } }),
    axios.get(`${base}/dashboard/payments-today`, { params: { date: prev, transaction_type: txType.value } }),
  ])
  acc.invoices = c1.data
  acc.payments = c2.data
  acc.uninvoiced = c3.data.count || 0
  acc.balPerClient = c4.data.results || []
  acc.totalCredit  = (c5.data && c5.data.total_credit) || 0
  acc.overdue = Number((c5.data && c5.data.overdue_credit) || 0)
  accAging.al_dia = Number(aging.data?.al_dia || 0)
  accAging.d7 = Number(aging.data?.d7 || 0)
  accAging.d14 = Number(aging.data?.d14 || 0)
  accAging.d21 = Number(aging.data?.d21 || 0)
  accAging.over_21 = Number(aging.data?.over_21 || 0)
  accPrev.invoices = Number(p1.data?.summary?.count || 0)
  accPrev.payments = Number(p2.data?.summary?.total_amount || 0)
}

async function fetchRRHH() {
  const base = apiBase()
  const r = await axios.get(`${base}/dashboard/ot-per-clerk`, { params: { date: rrhhDate.value } })
  rrhh.perClerk = r.data?.results || []
}

async function fetchRRHHMonthly() {
  const base = apiBase()

  const [yearStr, monthStr] = rrhhMonthYear.value.split("-")
  const year = Number(yearStr)
  const month = Number(monthStr)
  const prevYm = shiftMonth(rrhhMonthYear.value, -1)
  const [py, pm] = prevYm.split('-').map(Number)

  const [r, configRes, prevRes] = await Promise.all([
    axios.get(`${base}/dashboard/rrhh-monthly-clerk-series`, { params: { year, month } }),
    axios.get(`${base}/dashboard/call-center-capture-config`).catch(() => null),
    axios.get(`${base}/dashboard/rrhh-monthly-clerk-series`, { params: { year: py, month: pm } }).catch(() => null),
  ])

  const config = configRes && configRes.data
  captureConfig.configured = !!(config && config.is_configured)
  captureConfig.ids = Array.isArray(config && config.visible_clerk_ids) ? config.visible_clerk_ids : []
  captureConfig.shifts = (config && config.shift_by_clerk_id) || {}

  rrhhMonthlyDays.value = r.data?.days || []
  rrhhMonthlySeries.value = r.data?.series || []

  const prevSeries = (prevRes && prevRes.data && prevRes.data.series) || []
  rrhhPrev.month = prevSeries.filter(row => inCaptureRoster(row.clerk_id)).reduce((s, row) => s + Number(row.total || 0), 0)
  await fetchAttendance()
}

const attendanceSeries = ref([])
const attendanceDates = ref([])
const attendanceVisibleIds = ref(null)
const attendanceGroup = ref('')
const attendanceLoading = ref(false)
const attendanceError = ref('')
const attendanceLegend = [
  { code: 'N', label: 'Turno normal' },
  { code: 'F', label: 'Falta' },
  { code: 'R', label: 'Retraso' },
  { code: 'D', label: 'Turno doble' },
  { code: 'T', label: 'Turno triple' },
  { code: 'S', label: 'Suspendido' },
  { code: 'V', label: 'Vacaciones' },
  { code: 'X', label: 'Descanso' },
  { code: 'P', label: 'Permiso' },
  { code: 'I', label: 'Incapacidad' },
]

function attendanceStatusClass(code) {
  const value = String(code || '-').toUpperCase()
  if (value === 'N' || value === 'A') return 'status-a'
  if (value === 'F') return 'status-f'
  if (value === 'R') return 'status-r'
  if (value === 'D') return 'status-d'
  if (value === 'T') return 'status-t'
  if (value === 'S') return 'status-s'
  if (value === 'V' || value === 'X') return 'status-v'
  if (value === 'P') return 'status-p'
  if (value === 'I') return 'status-i'
  return 'status-empty'
}
function attendanceGroupName(group) {
  const value = String(group || '').trim()
  return value || 'Sin grupo'
}

const attendanceVisible = computed(() => {
  const ids = attendanceVisibleIds.value
  return (attendanceSeries.value || []).filter(row => ids === null || ids.has(Number(row.employee_id)))
})
const attendanceTabs = computed(() => {
  const groups = new Map()
  for (const row of attendanceVisible.value) {
    const key = attendanceGroupName(row.attendance_group)
    const existing = groups.get(key)
    if (existing) existing.count += 1
    else groups.set(key, { key, label: key, count: 1 })
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'))
})
const attendanceRows = computed(() => {
  const group = attendanceGroup.value
  const rows = attendanceVisible.value.filter(row => attendanceGroupName(row.attendance_group) === group)
  return [...rows].sort((a, b) => String(a.employee_ident || '').localeCompare(String(b.employee_ident || ''), 'es', { numeric: true }))
})
const attendanceHeaders = computed(() =>
  (attendanceDates.value || []).map(iso => fmtDate(iso).slice(0, 5))
)
watch(attendanceTabs, (tabs) => {
  if (!tabs.length) {
    attendanceGroup.value = ''
    return
  }
  if (!tabs.some(tab => tab.key === attendanceGroup.value)) attendanceGroup.value = tabs[0].key
})

async function fetchAttendance() {
  attendanceLoading.value = true
  attendanceError.value = ''
  try {
    const base = apiBase()
    const [matrix, config] = await Promise.all([
      axios.get(`${base}/dashboard/rrhh-attendance-monthly-matrix`, {
        params: { month: rrhhMonthYear.value, board: 'master' },
      }),
      axios.get(`${base}/dashboard/rrhh-attendance-config`, {
        params: { board: 'master' },
      }).catch(() => null),
    ])
    attendanceSeries.value = matrix.data?.series || []
    attendanceDates.value = matrix.data?.dates || []
    const data = config && config.data
    if (data && data.is_configured) {
      attendanceVisibleIds.value = new Set((data.visible_employee_ids || []).map(Number))
    } else {
      attendanceVisibleIds.value = null
    }
  } catch (e) {
    attendanceError.value = 'No se pudo cargar la asistencia.'
    attendanceSeries.value = []
    attendanceDates.value = []
  } finally {
    attendanceLoading.value = false
  }
}

async function fetchEstadisticoClientes() {
  const base = apiBase()
  const month = estadisticoMonth.value
  const year = Number(String(month || '').slice(0, 4))
  const loadYear = Number.isFinite(year) && year !== estadisticoYearly.loadedYear
  const prevMonth = shiftMonth(month, -1)
  const days = elapsedDayCount(month)

  const monthlyReq = axios.get(`${base}/dashboard/estadistico-clientes-monthly`, { params: { month } })
  const prevReq = axios.get(`${base}/dashboard/estadistico-clientes-monthly`, { params: { month: prevMonth } })
  const yearReq = loadYear
    ? axios.get(`${base}/dashboard/estadistico-clientes-yearly`, { params: { year } })
    : null

  const [monthly, previous, yearly] = await Promise.all([monthlyReq, prevReq, yearReq])
  estadistico.days = monthly.data?.days || []
  estadistico.countRows = monthly.data?.counts?.rows || []
  estadistico.countTotalDaily = monthly.data?.counts?.total_daily || []
  estadistico.countGrandTotal = Number(monthly.data?.counts?.grand_total || 0)
  estadistico.subtotalRows = monthly.data?.subtotals?.rows || []
  estadistico.subtotalTotalDaily = monthly.data?.subtotals?.total_daily || []
  estadistico.subtotalGrandTotal = Number(monthly.data?.subtotals?.grand_total || 0)

  const prevCounts = previous.data?.counts?.total_daily || []
  const prevSubs = previous.data?.subtotals?.total_daily || []
  estadisticoPrev.count = prevCounts.slice(0, days).reduce((s, v) => s + Number(v || 0), 0)
  estadisticoPrev.subtotal = prevSubs.slice(0, days).reduce((s, v) => s + Number(v || 0), 0)

  if (yearly) {
    estadisticoYearly.countRows = yearly.data?.counts?.rows || []
    estadisticoYearly.countTotalMonthly = yearly.data?.counts?.total_monthly || []
    estadisticoYearly.countGrandTotal = Number(yearly.data?.counts?.grand_total || 0)
    estadisticoYearly.subtotalRows = yearly.data?.subtotals?.rows || []
    estadisticoYearly.subtotalTotalMonthly = yearly.data?.subtotals?.total_monthly || []
    estadisticoYearly.subtotalGrandTotal = Number(yearly.data?.subtotals?.grand_total || 0)
    estadisticoYearly.loadedYear = year
  }
}


async function fetchOpsMonthly() {
  const base = apiBase()
  const prev = shiftMonth(opsMonth.value, -1)
  const [c1, c2, c3, p1, p2] = await Promise.all([
    axios.get(`${base}/dashboard/ot-daily-count`, { params: { month: opsMonth.value } }),
    axios.get(`${base}/dashboard/subtotal-daily-sum`, { params: { month: opsMonth.value } }),
    axios.get(`${base}/dashboard/insurer-services-daily`, { params: { month: opsMonth.value } }),
    axios.get(`${base}/dashboard/ot-daily-count`, { params: { month: prev } }),
    axios.get(`${base}/dashboard/subtotal-daily-sum`, { params: { month: prev } }),
  ])
  ops.dailyCount = c1.data.results || []
  ops.dailySubtotal = c2.data.results || []
  ops.insurerDaily = c3.data.results || []
  ops.insurerTotals = c3.data.totals || {}
  opsPrevMonth.dailyCount = p1.data.results || []
  opsPrevMonth.dailySubtotal = p2.data.results || []
}

async function fetchOperationsMonthly() {
  const base = apiBase()
  const r = await axios.get(`${base}/dashboard/ops-monthly-series`, {
    params: { year: opsYear.value }
  })
  ops.monthlyCount = r.data?.count_per_month || []
  ops.monthlySubtotal = r.data?.subtotal_per_month || []
}

const maintDate = ref(opsDate.value)

const maint = reactive({
  perUnitCount: [],   // {unit_short_id, count}
  perUnitMoney: [],   // {unit_short_id, amount}
  perUnitKm: [],      // {unit_short_id, km}
  byPrefix: []        // {prefix, count}
})

async function fetchMaintenance() {
  const base = apiBase()
  const prev = shiftISO(maintDate.value, -1)
  const [a, b, c, d, ap, bp, cp] = await Promise.all([
    axios.get(`${base}/dashboard/maint-assignations-per-unit`, { params: { date: maintDate.value } }),
    axios.get(`${base}/dashboard/maint-money-per-unit`,        { params: { date: maintDate.value } }),
    axios.get(`${base}/dashboard/maint-km-per-unit`,           { params: { date: maintDate.value } }),
    axios.get(`${base}/dashboard/maint-ot-by-prefix`,          { params: { date: maintDate.value } }),
    axios.get(`${base}/dashboard/maint-assignations-per-unit`, { params: { date: prev } }),
    axios.get(`${base}/dashboard/maint-money-per-unit`,        { params: { date: prev } }),
    axios.get(`${base}/dashboard/maint-km-per-unit`,           { params: { date: prev } }),
  ])
  maint.perUnitCount = a.data?.results || []
  maint.perUnitMoney = b.data?.results || []
  maint.perUnitKm    = c.data?.results || []
  maint.byPrefix     = d.data?.results || []
  const prevCount = ap.data?.results || []
  const prevMoney = bp.data?.results || []
  const prevKm = cp.data?.results || []
  maintPrev.count = prevCount.reduce((s, r) => s + Number(r.count || 0), 0)
  maintPrev.money = prevMoney.reduce((s, r) => s + Number(r.amount || 0), 0)
  maintPrev.km = prevKm.reduce((s, r) => s + Number(r.km || 0), 0)
  maintPrev.units = prevCount.length
}

/* ── Unidades: per-unit totals laid out in category columns ────────────────── */
const maintSections = [
  { key: 'graficas', label: 'Gráficas' },
  { key: 'rendimiento', label: 'Rendimiento por unidad' },
]
const maintSection = ref('graficas')

const unitStatsStart = ref(todayISO.slice(0, 8) + '01')
const unitStatsEnd = ref(todayISO)
const unitStatsSort = ref('services')
const unitStatsOnlyActive = ref(false)
const unitStatsOnlyIdle = ref(false)
const unitStatsLoading = ref(false)
const unitStats = ref([])   // {id, unit_short_id, unit_category, services, money, km, last_service_at}

// Category buckets, mirroring the unit_category values used by the units panel
const UNIT_CATEGORY_COLUMNS = [
  { key: 'ab', title: 'Tipo A/B', categories: ['A', 'B'] },
  { key: 'cd', title: 'Tipo C/D', categories: ['C', 'D'] },
  { key: 'montacargas', title: 'Montacargas', categories: ['MONTACARGAS'] },
  { key: 'otro', title: 'Otro', categories: null },   // catch-all
]
const unitStatsCategory = ref('ab')

// The two activity toggles are opposites, so only one can be on at a time
watch(unitStatsOnlyActive, on => { if (on) unitStatsOnlyIdle.value = false })
watch(unitStatsOnlyIdle, on => { if (on) unitStatsOnlyActive.value = false })

// A unit idle for this long gets the same red treatment as the units panel
const UNIT_STALE_HOURS = 24
const nowMs = ref(Date.now())

function hoursSinceNow(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return (nowMs.value - t) / 3600000
}

function isUnitStale(unit) {
  const h = hoursSinceNow(unit.last_service_at)
  return h !== null && h >= UNIT_STALE_HOURS
}

// Idle = nothing in the selected range, or flagged red for being stale
function isUnitIdle(unit) {
  return !Number(unit.services || 0) || isUnitStale(unit)
}

function elapsedLabel(iso) {
  const h = hoursSinceNow(iso)
  if (h === null) return ''
  if (h < 1) return `hace ${Math.max(1, Math.round(h * 60))} min`
  if (h < 48) return `hace ${Math.round(h)} h`
  const days = Math.floor(h / 24)
  const rest = Math.round(h % 24)
  return rest ? `hace ${days} d ${rest} h` : `hace ${days} d`
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

async function fetchUnitStats() {
  unitStatsLoading.value = true
  try {
    const start = unitStatsStart.value
    const end = unitStatsEnd.value
    const days = Math.max(1, Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1)
    const prevEnd = shiftISO(start, -1)
    const prevStart = shiftISO(prevEnd, -(days - 1))
    const [r, prev] = await Promise.all([
      axios.get(`${apiBase()}/dashboard/maint-unit-stats-by-category`, {
        params: { start_date: start, end_date: end }
      }),
      axios.get(`${apiBase()}/dashboard/maint-unit-stats-by-category`, {
        params: { start_date: prevStart, end_date: prevEnd }
      }),
    ])
    unitStats.value = r.data?.results || []
    const prevRows = prev.data?.results || []
    unitStatsPrev.units = prevRows.filter(u => Number(u.services || 0) > 0).length
    unitStatsPrev.services = prevRows.reduce((s, u) => s + Number(u.services || 0), 0)
    unitStatsPrev.money = prevRows.reduce((s, u) => s + Number(u.money || 0), 0)
    unitStatsPrev.km = prevRows.reduce((s, u) => s + Number(u.km || 0), 0)
  } finally {
    unitStatsLoading.value = false
  }
}

let unitStatsClock = null
onMounted(() => { unitStatsClock = setInterval(() => { nowMs.value = Date.now() }, 30000) })
onUnmounted(() => { if (unitStatsClock) clearInterval(unitStatsClock) })

const unitStatsTotals = computed(() => {
  const rows = unitStats.value || []
  return {
    units: rows.filter(u => Number(u.services || 0) > 0).length,
    services: rows.reduce((s, u) => s + Number(u.services || 0), 0),
    money: rows.reduce((s, u) => s + Number(u.money || 0), 0),
    km: rows.reduce((s, u) => s + Number(u.km || 0), 0),
  }
})

function sortUnitStats(list) {
  const byUnit = (a, b) => String(a.unit_short_id).localeCompare(String(b.unit_short_id), undefined, { numeric: true })
  if (unitStatsSort.value === 'unit') return [...list].sort(byUnit)
  const key = unitStatsSort.value
  return [...list].sort((a, b) => (Number(b[key] || 0) - Number(a[key] || 0)) || byUnit(a, b))
}

function unitCategoryKey(unit) {
  const cat = (unit.unit_category || '').toUpperCase()
  const col = UNIT_CATEGORY_COLUMNS.find(c => c.categories && c.categories.includes(cat))
  return col ? col.key : 'otro'
}

// Rows surviving the activity toggles, bucketed by category
const unitStatsBuckets = computed(() => {
  const buckets = {}
  for (const col of UNIT_CATEGORY_COLUMNS) buckets[col.key] = []
  for (const u of unitStats.value || []) {
    if (unitStatsOnlyActive.value && !Number(u.services || 0)) continue
    if (unitStatsOnlyIdle.value && !isUnitIdle(u)) continue
    buckets[unitCategoryKey(u)].push(u)
  }
  return buckets
})

const unitStatsCounts = computed(() => {
  const counts = {}
  for (const col of UNIT_CATEGORY_COLUMNS) counts[col.key] = unitStatsBuckets.value[col.key].length
  return counts
})

const unitStatsVisible = computed(() =>
  sortUnitStats(unitStatsBuckets.value[unitStatsCategory.value] || [])
)

const creditMonth = ref(rrhhMonthYear.value)

const accCreditSeries = ref([])

async function fetchCreditTimeseries() {
  const base = apiBase()
  const r = await axios.get(`${base}/dashboard/credit-timeseries`, {
    params: { month: creditMonth.value } // "YYYY-MM"
  })
  accCreditSeries.value = r.data.points || []
}

const uninvoicedMonth = ref(todayISO.slice(0, 7))
const accUninvoicedSubtotalSeries = ref([])

async function fetchUninvoicedSubtotalDaily() {
  const base = apiBase()
  const r = await axios.get(`${base}/dashboard/uninvoiced-subtotal-daily`, {
    params: { month: uninvoicedMonth.value }
  })
  accUninvoicedSubtotalSeries.value = r.data.points || []
}

const accentColor = computed(() => (isDark.value ? '#ffc434' : '#9a7209'))
const statusColor = computed(() => (isDark.value
  ? { ok: '#4ade80', warn: '#fbbf24', bad: '#f87171' }
  : { ok: '#15803d', warn: '#b45309', bad: '#dc2626' }))

const monthLabels = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

function sparkOption(values) {
  const data = (values || []).map(v => Number(v) || 0)
  if (data.length < 2) return null
  const color = accentColor.value
  return {
    animation: false,
    grid: { left: 0, right: 0, top: 4, bottom: 2 },
    xAxis: { type: 'category', show: false, boundaryGap: false, data: data.map((_, i) => i) },
    yAxis: { type: 'value', show: false, scale: true },
    series: [{
      type: 'line',
      data,
      smooth: false,
      showSymbol: false,
      lineStyle: { width: 1.6, color },
      areaStyle: { color: color + '33' },
    }],
  }
}

function barHeight(n) {
  return `${Math.max(168, n * 28 + 24)}px`
}

function hBarOption(items, { isMoney = false, identity = false } = {}) {
  const labels = items.map(i => i.name)
  const accent = accentColor.value
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const p = params[0]
        const item = items[p.dataIndex] || {}
        const val = isMoney ? money(p.value) : fmtInt(p.value)
        const note = item.overdue > 0 ? '<br/>Incluye saldo vencido' : ''
        return `${p.axisValue}<br/><b>${val}</b>${note}`
      },
    },
    grid: { left: 4, right: 16, top: 4, bottom: 4, containLabel: true },
    xAxis: {
      type: 'value',
      splitNumber: 3,
      axisLabel: isMoney ? { formatter: v => fmtMoney0(v) } : { formatter: v => fmtInt(v) },
    },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisTick: { show: false },
      axisLabel: { width: 148, overflow: 'truncate' },
    },
    series: [{
      type: 'bar',
      barMaxWidth: 16,
      data: items.map(i => ({
        value: i.value,
        itemStyle: {
          color: i.name === 'Otros' ? OTROS_COLOR : (identity ? entityColor(i.name) : accent),
          borderRadius: [0, 3, 3, 0],
        },
      })),
    }],
  }
}

function alignedCompare(currentRows, compareRows, valueKey) {
  const map = {}
  for (const r of compareRows || []) {
    const day = Number(String(r.date).slice(8, 10))
    if (day) map[day] = Number(r[valueKey] || 0)
  }
  return currentRows.map(r => {
    const day = Number(String(r.date).slice(8, 10))
    return Object.prototype.hasOwnProperty.call(map, day) ? map[day] : null
  })
}

function compareRowsFor(kind) {
  if (compareMode.value === 'year') {
    if (kind === 'count') return opsYearCompare.dailyCount
    if (kind === 'amount') return opsYearCompare.dailySubtotal
    if (kind === 'insurer') return opsYearCompare.insurerDaily || []
  }
  if (compareMode.value === 'month') {
    if (kind === 'count') return opsPrevMonth.dailyCount
    if (kind === 'amount') return opsPrevMonth.dailySubtotal
  }
  return []
}

function buildDailyLine({ rows, month, valueKey, compareRows, isMoney, seriesName }) {
  const elapsed = clipRows(rows, month)
  const labels = elapsed.map(r => fmtDate(r.date).slice(0, 5))
  const values = elapsed.map(r => Number(r[valueKey] || 0))
  const avg = values.length ? values.reduce((s, n) => s + n, 0) / values.length : null
  const mask = outlierMask(values)
  const showCompare = compareMode.value !== 'none' && (compareRows || []).length
  const compareName = compareMode.value === 'year' ? 'Año anterior' : 'Mes anterior'
  const compareData = showCompare ? alignedCompare(elapsed, compareRows, valueKey) : null
  const fmt = isMoney ? fmtMoney0 : fmtInt
  const accent = accentColor.value
  const warn = statusColor.value.warn
  const series = [{
    name: seriesName,
    type: 'line',
    smooth: false,
    showSymbol: mask.some(Boolean),
    symbol: 'circle',
    symbolSize: (_v, params) => (mask[params.dataIndex] ? 9 : 0),
    data: values.map((v, i) => ({ value: v, itemStyle: { color: mask[i] ? warn : accent } })),
    lineStyle: { color: accent, width: 2 },
    areaStyle: { color: areaGradient(accent) },
    markLine: avg == null ? undefined : {
      symbol: 'none',
      silent: true,
      lineStyle: { type: 'dashed', color: 'rgba(148,163,184,0.7)', width: 1 },
      label: { formatter: `Prom. ${fmt(avg)}`, color: '#94a3b8', fontSize: 10, position: 'insideEndTop' },
      data: [{ yAxis: avg }],
    },
  }]
  if (compareData) {
    series.push({
      name: compareName,
      type: 'line',
      smooth: false,
      showSymbol: false,
      data: compareData,
      lineStyle: { type: 'dashed', width: 1.5, color: COMPARE_COLOR },
      itemStyle: { color: COMPARE_COLOR },
    })
  }
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const i = params[0]?.dataIndex ?? 0
        const date = elapsed[i] ? fmtDate(elapsed[i].date) : ''
        const lines = params.map(p => {
          const raw = p.value && typeof p.value === 'object' ? p.value.value : p.value
          const text = isMoney ? money(raw) : fmtInt(raw)
          return `${p.marker} ${p.seriesName}: <b>${text}</b>`
        })
        if (mask[i]) lines.push('Valor atípico')
        return `${date}<br/>${lines.join('<br/>')}`
      },
    },
    legend: { show: !!compareData, top: 0, itemWidth: 16, itemHeight: 8 },
    grid: { left: 8, right: 12, top: compareData ? 28 : 16, bottom: 8, containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: labels, axisTick: { show: false } },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      axisLabel: isMoney ? { formatter: v => fmtMoney0(v) } : { formatter: v => fmtInt(v) },
    },
    series,
  }
}

function buildYearLine({ values, year, compareValues, isMoney, seriesName }) {
  const elapsed = clipYear(values, year)
  const labels = monthLabels.slice(0, elapsed.length)
  const avg = elapsed.length ? elapsed.reduce((s, n) => s + n, 0) / elapsed.length : null
  const mask = outlierMask(elapsed)
  const showCompare = compareMode.value === 'year' && (compareValues || []).length
  const compareData = showCompare ? compareValues.slice(0, elapsed.length) : null
  const fmt = isMoney ? fmtMoney0 : fmtInt
  const accent = accentColor.value
  const warn = statusColor.value.warn
  const series = [{
    name: seriesName,
    type: 'line',
    smooth: false,
    showSymbol: mask.some(Boolean),
    symbol: 'circle',
    symbolSize: (_v, params) => (mask[params.dataIndex] ? 9 : 0),
    data: elapsed.map((v, i) => ({ value: v, itemStyle: { color: mask[i] ? warn : accent } })),
    lineStyle: { color: accent, width: 2 },
    areaStyle: { color: areaGradient(accent) },
    markLine: avg == null ? undefined : {
      symbol: 'none',
      silent: true,
      lineStyle: { type: 'dashed', color: 'rgba(148,163,184,0.7)', width: 1 },
      label: { formatter: `Prom. ${fmt(avg)}`, color: '#94a3b8', fontSize: 10, position: 'insideEndTop' },
      data: [{ yAxis: avg }],
    },
  }]
  if (compareData) {
    series.push({
      name: 'Año anterior',
      type: 'line',
      smooth: false,
      showSymbol: false,
      data: compareData,
      lineStyle: { type: 'dashed', width: 1.5, color: COMPARE_COLOR },
      itemStyle: { color: COMPARE_COLOR },
    })
  }
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const i = params[0]?.dataIndex ?? 0
        const lines = params.map(p => {
          const raw = p.value && typeof p.value === 'object' ? p.value.value : p.value
          return `${p.marker} ${p.seriesName}: <b>${isMoney ? money(raw) : fmtInt(raw)}</b>`
        })
        if (mask[i]) lines.push('Valor atípico')
        return `${labels[i] || ''} ${year}<br/>${lines.join('<br/>')}`
      },
    },
    legend: { show: !!compareData, top: 0 },
    grid: { left: 8, right: 12, top: compareData ? 28 : 16, bottom: 8, containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: labels, axisTick: { show: false } },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      axisLabel: isMoney ? { formatter: v => fmtMoney0(v) } : { formatter: v => fmtInt(v) },
    },
    series,
  }
}

const opsDaySubtotal = computed(() =>
  (ops.sucursalSubtotal || []).reduce((s, r) => s + Number(r.amount || 0), 0)
)
const opsMonthElapsed = computed(() => elapsedDayCount(opsMonth.value))
const opsMonthOrders = computed(() => sumFirstDays(clipRows(ops.dailyCount, opsMonth.value), 31, 'count'))

const opsKpis = computed(() => {
  const days = opsMonthElapsed.value
  const prevOrders = sumFirstDays(opsPrevMonth.dailyCount, days, 'count')
  return [
    {
      key: 'ot',
      label: 'OT del día',
      value: fmtInt(opsTotalOrders.value),
      delta: periodDelta(opsTotalOrders.value, opsPrev.orders),
      hint: 'vs. día anterior',
      spark: sparkOption(clipRows(ops.dailyCount, opsMonth.value).map(r => r.count)),
    },
    {
      key: 'sub',
      label: 'Subtotal del día',
      value: money(opsDaySubtotal.value),
      delta: periodDelta(opsDaySubtotal.value, opsPrev.subtotal),
      hint: 'vs. día anterior',
      spark: sparkOption(clipRows(ops.dailySubtotal, opsMonth.value).map(r => r.amount)),
    },
    {
      key: 'month',
      label: 'OT del mes',
      value: fmtInt(opsMonthOrders.value),
      delta: periodDelta(opsMonthOrders.value, prevOrders),
      hint: 'mismos días, mes anterior',
      spark: sparkOption(clipRows(ops.dailyCount, opsMonth.value).map(r => r.count)),
    },
  ]
})

const invSummarySplit = computed(() => {
  const s = acc.invoices?.summary || {}
  let credit = s.total_amount_credit
  let contado = s.total_amount_contado
  const rows = acc.invoices?.results || []

  if (credit == null || contado == null) {
    let c = 0
    let k = 0
    for (const r of rows) {
      const amt = Number(r.amount || 0)
      if ((r.invoice_type || '').toLowerCase() === 'credito') c += amt
      else if ((r.invoice_type || '').toLowerCase() === 'contado') k += amt
    }
    credit = credit ?? c
    contado = contado ?? k
  }

  return {
    count: s.count ?? rows.length,
    total_balance: s.total_balance ?? 0,
    total_amount_credit: credit || 0,
    total_amount_contado: contado || 0,
  }
})

const accHeadlineKpis = computed(() => {
  const creditPts = clipRows(accCreditSeries.value.map(p => ({ date: p.date, credit: p.credit })), creditMonth.value)
  const prevCredit = creditPts.length > 1 ? creditPts[creditPts.length - 2].credit : null
  const lastCredit = creditPts.length ? creditPts[creditPts.length - 1].credit : acc.totalCredit
  const pendingPts = clipRows(
    (accUninvoicedSubtotalSeries.value || []).map(p => ({ date: p.date, subtotal: p.subtotal })),
    uninvoicedMonth.value,
  )
  const prevPending = pendingPts.length > 1 ? pendingPts[pendingPts.length - 2].subtotal : null
  return [
    {
      key: 'unbilled-count',
      label: 'Pendiente por facturar (órdenes)',
      value: fmtInt(pendingBillingCount.value),
      delta: pendingBillingCount.value > 0 ? { text: 'Órdenes sin factura', cls: 'flat' } : { text: 'Al corriente', cls: 'flat' },
      hint: 'cantidad',
      alert: pendingBillingCount.value > 0,
      spark: null,
    },
    {
      key: 'unbilled-money',
      label: 'Pendiente por facturar (dinero)',
      value: money(pendingBillingTotal.value),
      delta: periodDelta(pendingBillingTotal.value, prevPending),
      hint: 'vs. día anterior',
      alert: pendingBillingTotal.value > 0,
      spark: sparkOption(pendingPts.map(p => p.subtotal)),
    },
    {
      key: 'credit',
      label: 'Cartera',
      value: money(acc.totalCredit),
      delta: periodDelta(lastCredit, prevCredit),
      hint: 'vs. día anterior',
      spark: sparkOption(creditPts.map(p => p.credit)),
    },
  ]
})

const accAgingKpis = computed(() => [
  { key: 'al_dia', label: 'Al día', value: money(accAging.al_dia), delta: { text: 'Dentro del plazo', cls: 'flat' }, hint: 'antigüedad de saldos', alert: false },
  { key: 'd7', label: 'A 7 días', value: money(accAging.d7), delta: { text: 'Hasta 7 días de atraso', cls: 'flat' }, hint: 'antigüedad de saldos', alert: false },
  { key: 'd14', label: 'A 14 días', value: money(accAging.d14), delta: { text: 'De 8 a 14 días', cls: 'flat' }, hint: 'antigüedad de saldos', alert: false },
  { key: 'd21', label: 'A 21 días', value: money(accAging.d21), delta: { text: 'De 15 a 21 días', cls: 'flat' }, hint: 'antigüedad de saldos', alert: false },
  {
    key: 'over_21',
    label: 'Más de 21 días',
    value: money(accAging.over_21),
    delta: accAging.over_21 > 0 ? { text: 'Saldo con más atraso', cls: 'flat' } : { text: 'Sin saldo en este rango', cls: 'flat' },
    hint: 'antigüedad de saldos',
    alert: accAging.over_21 > 0,
  },
])

const maintAssignTotal = computed(() =>
  (maint.perUnitCount || []).reduce((s, r) => s + Number(r.count || 0), 0)
)
const maintKpis = computed(() => [
  {
    key: 'asig',
    label: 'Asignaciones',
    value: fmtInt(maintAssignTotal.value),
    delta: periodDelta(maintAssignTotal.value, maintPrev.count),
    hint: 'vs. día anterior',
    spark: null,
  },
  {
    key: 'money',
    label: 'Monto',
    value: money(maintTotalMoney.value),
    delta: periodDelta(maintTotalMoney.value, maintPrev.money),
    hint: 'vs. día anterior',
    spark: null,
  },
  {
    key: 'km',
    label: 'Kilómetros',
    value: fmtInt(maintTotalKm.value),
    delta: periodDelta(maintTotalKm.value, maintPrev.km),
    hint: 'vs. día anterior',
    spark: null,
  },
  {
    key: 'units',
    label: 'Unidades',
    value: fmtInt(maint.perUnitCount.length),
    delta: periodDelta(maint.perUnitCount.length, maintPrev.units),
    hint: 'vs. día anterior',
    spark: null,
  },
])

const unitKpis = computed(() => [
  {
    key: 'units',
    label: 'Unidades con actividad',
    value: fmtInt(unitStatsTotals.value.units),
    delta: periodDelta(unitStatsTotals.value.units, unitStatsPrev.units),
    hint: 'vs. periodo anterior',
    spark: null,
  },
  {
    key: 'services',
    label: 'Servicios',
    value: fmtInt(unitStatsTotals.value.services),
    delta: periodDelta(unitStatsTotals.value.services, unitStatsPrev.services),
    hint: 'vs. periodo anterior',
    spark: null,
  },
  {
    key: 'money',
    label: 'Monto',
    value: money(unitStatsTotals.value.money),
    delta: periodDelta(unitStatsTotals.value.money, unitStatsPrev.money),
    hint: 'vs. periodo anterior',
    spark: null,
  },
  {
    key: 'km',
    label: 'Kilómetros',
    value: fmtInt(unitStatsTotals.value.km),
    delta: periodDelta(unitStatsTotals.value.km, unitStatsPrev.km),
    hint: 'vs. periodo anterior',
    spark: null,
  },
])

const rrhhTodayPrev = computed(() => {
  if (!rrhhDate.value || !String(rrhhMonthYear.value).startsWith(rrhhDate.value.slice(0, 7))) return null
  const day = Number(rrhhDate.value.slice(8, 10))
  if (day < 2) return null
  return (rrhhMonthlySeries.value || []).filter(row => inCaptureRoster(row.clerk_id)).reduce((s, row) => s + Number((row.daily || [])[day - 2] || 0), 0)
})
const rrhhDailyTotals = computed(() => {
  const days = rrhhMonthlyDays.value || []
  const month = rrhhMonthYear.value
  return days
    .map((day, idx) => ({
      date: `${month}-${String(day).padStart(2, '0')}`,
      count: (rrhhMonthlySeries.value || []).filter(row => inCaptureRoster(row.clerk_id)).reduce((s, row) => s + Number((row.daily || [])[idx] || 0), 0),
    }))
    .filter(r => !isFutureDay(Number(r.date.slice(8, 10)), month))
})
const rrhhKpis = computed(() => [
  {
    key: 'today',
    label: 'OT hoy',
    value: fmtInt(rrhhTotalToday.value),
    delta: periodDelta(rrhhTotalToday.value, rrhhTodayPrev.value),
    hint: 'vs. día anterior',
    spark: sparkOption(rrhhDailyTotals.value.map(r => r.count)),
  },
  {
    key: 'month',
    label: 'OT del mes',
    value: fmtInt(rrhhMonthTotal.value),
    delta: periodDelta(rrhhMonthTotal.value, rrhhPrev.month),
    hint: 'vs. mes anterior',
    spark: sparkOption(rrhhDailyTotals.value.map(r => r.count)),
  },
  {
    key: 'clerks',
    label: 'Capturistas',
    value: fmtInt((rrhhMonthlySeries.value || []).filter(s => inCaptureRoster(s.clerk_id)).length),
    delta: { text: '', cls: 'flat' },
    hint: '',
    spark: null,
  },
  {
    key: 'avg',
    label: 'Promedio diario',
    value: fmtInt(rrhhDailyTotals.value.length
      ? rrhhDailyTotals.value.reduce((s, r) => s + r.count, 0) / rrhhDailyTotals.value.length
      : 0),
    delta: { text: 'días transcurridos del mes', cls: 'flat' },
    hint: '',
    spark: sparkOption(rrhhDailyTotals.value.map(r => r.count)),
  },
])

const estadisticoElapsedCount = computed(() => {
  const days = elapsedDayCount(estadisticoMonth.value)
  return (estadistico.countTotalDaily || []).slice(0, days).reduce((s, v) => s + Number(v || 0), 0)
})
const estadisticoElapsedSubtotal = computed(() => {
  const days = elapsedDayCount(estadisticoMonth.value)
  return (estadistico.subtotalTotalDaily || []).slice(0, days).reduce((s, v) => s + Number(v || 0), 0)
})
const estadisticoKpis = computed(() => [
  {
    key: 'services',
    label: 'Servicios',
    value: fmtInt(estadisticoElapsedCount.value),
    delta: periodDelta(estadisticoElapsedCount.value, estadisticoPrev.count),
    hint: 'mismos días, mes anterior',
    spark: sparkOption(clipYear(estadisticoYearly.countTotalMonthly, estadisticoYear.value)),
  },
  {
    key: 'income',
    label: 'Ingresos',
    value: money(estadisticoElapsedSubtotal.value),
    delta: periodDelta(estadisticoElapsedSubtotal.value, estadisticoPrev.subtotal),
    hint: 'mismos días, mes anterior',
    spark: sparkOption(clipYear(estadisticoYearly.subtotalTotalMonthly, estadisticoYear.value)),
  },
  {
    key: 'clients',
    label: 'Clientes',
    value: fmtInt(estadistico.countRows.length),
    delta: { text: 'con movimiento en el mes', cls: 'flat' },
    hint: estadisticoMonth.value ? fmtDate(estadisticoMonth.value + '-01').slice(3) : '',
    spark: null,
  },
  {
    key: 'days',
    label: 'Días transcurridos',
    value: fmtInt(elapsedDayCount(estadisticoMonth.value)),
    delta: { text: '', cls: 'flat' },
    hint: 'del mes seleccionado',
    spark: null,
  },
])

const adsTotals = computed(() => {
  const names = [...adsCampaigns.remolque, ...adsCampaigns.montacargas].map(c => c.name)
  let cost = 0
  let clicks = 0
  const byDate = new Map()
  for (const name of names) {
    const data = ads[name] && ads[name].data
    if (!data || !data.totals) continue
    cost += Number(data.totals.cost || 0)
    clicks += Number(data.totals.clicks || 0)
    for (const p of data.series || []) {
      if (p.date && p.date <= todayISO) {
        byDate.set(p.date, (byDate.get(p.date) || 0) + Number(p.cost || 0))
      }
    }
  }
  let services = 0
  let ganancia = 0
  for (const section of ['remolque', 'montacargas']) {
    const data = adsInternal[section] && adsInternal[section].data
    if (!data || !data.totals) continue
    services += Number(data.totals.services_closed || 0)
    ganancia += Number(data.totals.ganancia || 0)
  }
  const spark = [...byDate.entries()].sort((a, b) => a[0] < b[0] ? -1 : 1).map(([, v]) => v)
  return { cost, clicks, services, ganancia, spark }
})
const adsKpis = computed(() => [
  {
    key: 'cost',
    label: 'Costo de anuncios',
    value: money(adsTotals.value.cost),
    delta: periodDelta(adsTotals.value.cost, adsPrev.cost),
    hint: 'vs. periodo anterior',
    spark: sparkOption(adsTotals.value.spark),
  },
  {
    key: 'clicks',
    label: 'Clics',
    value: fmtInt(adsTotals.value.clicks),
    delta: periodDelta(adsTotals.value.clicks, adsPrev.clicks),
    hint: 'vs. periodo anterior',
    spark: null,
  },
  {
    key: 'services',
    label: 'Servicios cerrados',
    value: fmtInt(adsTotals.value.services),
    delta: periodDelta(adsTotals.value.services, adsPrev.services),
    hint: 'vs. periodo anterior',
    spark: null,
  },
  {
    key: 'ganancia',
    label: 'Ganancia',
    value: money(adsTotals.value.ganancia),
    delta: periodDelta(adsTotals.value.ganancia, adsPrev.ganancia),
    hint: 'vs. periodo anterior',
    spark: null,
  },
])

const clientBars = computed(() =>
  topWithOthers(ops.ordersPerClient, r => r.client_name_ph, r => r.orders)
)
const clientBarOption = computed(() => hBarOption(clientBars.value, { identity: true }))

const sucursalCountBars = computed(() =>
  topWithOthers(ops.sucursalCount, r => r.sucursal || 'Sin sucursal', r => r.count)
)
const sucursalCountOption = computed(() => hBarOption(sucursalCountBars.value, { identity: true }))

const sucursalSubtotalBars = computed(() =>
  topWithOthers(ops.sucursalSubtotal, r => r.sucursal || 'Sin sucursal', r => r.amount)
)
const sucursalSubtotalOption = computed(() => hBarOption(sucursalSubtotalBars.value, { isMoney: true, identity: true }))

const typeBars = computed(() =>
  topWithOthers(ops.typeFrequency, r => r.type_name || 'Sin tipo', r => r.count)
)
const typeFreqOption = computed(() => hBarOption(typeBars.value))

const otDailyCountOption = computed(() => buildDailyLine({
  rows: ops.dailyCount,
  month: opsMonth.value,
  valueKey: 'count',
  compareRows: compareRowsFor('count'),
  seriesName: 'Órdenes',
}))
const subtotalDailySumOption = computed(() => buildDailyLine({
  rows: ops.dailySubtotal,
  month: opsMonth.value,
  valueKey: 'amount',
  compareRows: compareRowsFor('amount'),
  isMoney: true,
  seriesName: 'Subtotal',
}))

const opsMonthlyCountOption = computed(() => {
  const data = new Array(12).fill(0)
  for (const row of ops.monthlyCount || []) {
    const idx = (row.month || 0) - 1
    if (idx >= 0 && idx < 12) data[idx] = Number(row.count || 0)
  }
  const compare = new Array(12).fill(0)
  for (const row of opsYearCompare.monthlyCount || []) {
    const idx = (row.month || 0) - 1
    if (idx >= 0 && idx < 12) compare[idx] = Number(row.count || 0)
  }
  return buildYearLine({
    values: data,
    year: opsYear.value,
    compareValues: compare,
    seriesName: 'Órdenes',
  })
})
const opsMonthlySubtotalOption = computed(() => {
  const data = new Array(12).fill(0)
  for (const row of ops.monthlySubtotal || []) {
    const idx = (row.month || 0) - 1
    if (idx >= 0 && idx < 12) data[idx] = Number(row.subtotal || 0)
  }
  const compare = new Array(12).fill(0)
  for (const row of opsYearCompare.monthlySubtotal || []) {
    const idx = (row.month || 0) - 1
    if (idx >= 0 && idx < 12) compare[idx] = Number(row.subtotal || 0)
  }
  return buildYearLine({
    values: data,
    year: opsYear.value,
    compareValues: compare,
    isMoney: true,
    seriesName: 'Subtotal',
  })
})

const insurerDailyOption = computed(() => {
  const rows = clipRows(ops.insurerDaily || [], opsMonth.value)
  const colors = statusColor.value
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const idx = params[0]?.dataIndex ?? 0
        const r = rows[idx] || {}
        const lines = params.map(p => `${p.marker} ${p.seriesName}: <b>${fmtInt(p.value)}</b>`)
        lines.push(`Total recibidos: <b>${fmtInt(r.total || 0)}</b>`)
        return `${fmtDate(r.date)}<br/>${lines.join('<br/>')}`
      },
    },
    legend: { data: ['Aceptados', 'Tiempo agotado', 'Con error'], top: 0 },
    grid: { left: 8, right: 8, top: 28, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: rows.map(r => fmtDate(r.date).slice(0, 5)), axisTick: { show: false } },
    yAxis: { type: 'value', splitNumber: 4, minInterval: 1 },
    series: [
      { name: 'Aceptados', type: 'bar', stack: 'axa', barMaxWidth: 18, itemStyle: { color: colors.ok }, data: rows.map(r => Number(r.accepted || 0)) },
      { name: 'Tiempo agotado', type: 'bar', stack: 'axa', barMaxWidth: 18, itemStyle: { color: colors.warn }, data: rows.map(r => Number(r.timed_out || 0)) },
      { name: 'Con error', type: 'bar', stack: 'axa', barMaxWidth: 18, itemStyle: { color: colors.bad }, data: rows.map(r => Number(r.errored || 0)) },
    ],
  }
})

const balanceBars = computed(() =>
  topWithOthers(acc.balPerClient, r => r.client_name, r => r.amount)
)
const balPerClientBarOption = computed(() => hBarOption(balanceBars.value, { isMoney: true, identity: true }))

const pendingBars = computed(() =>
  topWithOthers(pendingBillingByClient.value, r => r.client_name, r => r.amount)
)
const pendingBillingByClientOption = computed(() => hBarOption(pendingBars.value, { isMoney: true, identity: true }))

const accCreditTimeseriesOption = computed(() => buildDailyLine({
  rows: (accCreditSeries.value || []).map(p => ({ date: p.date, credit: p.credit })),
  month: creditMonth.value,
  valueKey: 'credit',
  compareRows: compareMode.value === 'none' ? [] : accCompare.credit,
  isMoney: true,
  seriesName: 'Cartera',
}))
const accUninvoicedSubtotalDailyOption = computed(() => buildDailyLine({
  rows: (accUninvoicedSubtotalSeries.value || []).map(p => ({ date: p.date, subtotal: p.subtotal })),
  month: uninvoicedMonth.value,
  valueKey: 'subtotal',
  compareRows: compareMode.value === 'none' ? [] : accCompare.uninvoiced,
  isMoney: true,
  seriesName: 'Pendiente por facturar',
}))

function unitBars(rows, valueKey) {
  const items = topWithOthers(rows, r => r.unit_short_id, r => r[valueKey])
  const named = items.filter(i => i.name !== 'Otros')
  const { normal, outliers } = splitScale(named)
  const otros = items.find(i => i.name === 'Otros')
  const plotted = [...normal]
  if (otros) plotted.push(otros)
  return { plotted, outliers }
}
const countUnitView = computed(() => unitBars(maint.perUnitCount, 'count'))
const moneyUnitView = computed(() => unitBars(maint.perUnitMoney, 'amount'))
const kmUnitView = computed(() => unitBars(maint.perUnitKm, 'km'))
const countPerUnitOption = computed(() => hBarOption(countUnitView.value.plotted))
const moneyPerUnitOption = computed(() => hBarOption(moneyUnitView.value.plotted, { isMoney: true }))
const kmPerUnitOption = computed(() => hBarOption(kmUnitView.value.plotted))

const prefixBars = computed(() =>
  [...(maint.byPrefix || [])]
    .map(r => ({ name: prefixLabel(r.prefix), value: Number(r.count || 0) }))
    .sort((a, b) => b.value - a.value)
)
const otByPrefixOption = computed(() => hBarOption(prefixBars.value))

function yearCell(value, idx, asMoney) {
  if (isFutureMonthIndex(idx, estadisticoYear.value)) return '—'
  return asMoney ? money(value) : fmtInt(value)
}
function dayCell(value, day, asMoney) {
  if (isFutureDay(day, estadisticoMonth.value)) return '—'
  return asMoney ? money(value) : fmtInt(value)
}
function invoiceTypeLabel(t) {
  const s = String(t || '').toLowerCase()
  if (s.includes('cred')) return 'Crédito'
  if (s.includes('cont')) return 'Contado'
  return t || '—'
}
const txTypeLabel = computed(() => TX_TYPES.find(t => t.id === Number(txType.value))?.label || 'Movimiento')
function wsLabel(status) {
  if (status === 'open') return 'en línea'
  if (status === 'retry') return 'reintentando'
  return 'desconectado'
}

async function fetchCompareSeries() {
  if (compareMode.value === 'none') return
  const base = apiBase()
  if (compareMode.value === 'year') {
    const month = shiftMonth(opsMonth.value, -12)
    const year = Number(opsYear.value) - 1
    const [c1, c2, c3, yearRes] = await Promise.all([
      axios.get(`${base}/dashboard/ot-daily-count`, { params: { month } }),
      axios.get(`${base}/dashboard/subtotal-daily-sum`, { params: { month } }),
      axios.get(`${base}/dashboard/insurer-services-daily`, { params: { month } }),
      axios.get(`${base}/dashboard/ops-monthly-series`, { params: { year } }),
    ])
    opsYearCompare.dailyCount = c1.data.results || []
    opsYearCompare.dailySubtotal = c2.data.results || []
    opsYearCompare.insurerDaily = c3.data.results || []
    opsYearCompare.monthlyCount = yearRes.data?.count_per_month || []
    opsYearCompare.monthlySubtotal = yearRes.data?.subtotal_per_month || []
  }
  const creditM = shiftMonth(creditMonth.value, compareMode.value === 'year' ? -12 : -1)
  const unbilledM = shiftMonth(uninvoicedMonth.value, compareMode.value === 'year' ? -12 : -1)
  const [creditRes, unbilledRes] = await Promise.all([
    axios.get(`${base}/dashboard/credit-timeseries`, { params: { month: creditM } }),
    axios.get(`${base}/dashboard/uninvoiced-subtotal-daily`, { params: { month: unbilledM } }),
  ])
  accCompare.credit = (creditRes.data.points || []).map(p => ({ date: p.date, credit: p.credit }))
  accCompare.uninvoiced = (unbilledRes.data.points || []).map(p => ({ date: p.date, subtotal: p.subtotal }))
}

watch(compareMode, () => { fetchCompareSeries() })
watch([opsMonth, opsYear], () => { if (compareMode.value === 'year') fetchCompareSeries() })
watch([creditMonth, uninvoicedMonth], () => { if (compareMode.value !== 'none') fetchCompareSeries() })


const rrhhMonthlyTotalsSorted = computed(() => {
  return [...(rrhhMonthlySeries.value || [])]
    .filter(s => inCaptureRoster(s.clerk_id))
    .sort((a, b) => (b.total || 0) - (a.total || 0))
})

const rrhhShiftGroups = computed(() => {
  const rows = rrhhMonthlyTotalsSorted.value
  if (!captureConfig.configured) return [{ key: 'all', label: '', rows }]
  return CAPTURE_SHIFTS
    .map(shift => ({
      key: shift.key,
      label: shift.label,
      rows: rows.filter(row => captureShift(row.clerk_id) === shift.key),
    }))
    .filter(group => group.rows.length)
})

function rrhhMatrixCellStyle(value) {
  const n = Number(value || 0)
  const dark = isDark.value
  if (!n) return { background: 'transparent', color: dark ? '#37425a' : '#cbd5e1' }
  const t = Math.min(n / 8, 1)
  const alpha = (0.18 + t * 0.62).toFixed(3)
  return {
    background: `rgba(255, 196, 52, ${alpha})`,
    color: t > 0.55 ? '#1c1404' : (dark ? '#f8fafc' : '#0f172a'),
    fontWeight: t > 0.45 ? 700 : 600,
  }
}

/* ───────────────────────── Publicidad (Google Ads) ───────────────────────── */
const adsCampaigns = {
  remolque: [
    { name: 'XL General Towing | Search', title: 'XL General Towing | Search' },
  ],
  montacargas: [
    { name: 'XL | Forklift Rental | Search', title: 'XL | Forklift Rental | Search' },
    { name: 'XL | Forklift Transport | Search', title: 'XL | Forklift Transport | Search' },
  ],
}

const ads = reactive({})          // keyed by campaign name -> { loading, data }
const adsInternal = reactive({})  // keyed by section -> { loading, data }
const adsServices = reactive({ loading: true, data: null })  // flat qualifying-services table
const adsNoteBaseUrl = `${(process.env.PUBLIC_BASE_URL || '').replace(/\/+$/, '')}/notes/`

// note-type ids that define each section's internal "Servicios cerrados / Ganancia"
const adsInternalTypes = { remolque: '1,2,4', montacargas: '3' }

const adsInternalMetrics = [
  { key: 'services_closed', label: 'Servicios cerrados', color: '#ffc434', axis: 'count', fmt: 'int' },
  { key: 'ganancia',        label: 'Ganancia',           color: '#64748b', axis: 'money', fmt: 'money' },
]

function isoDaysAgo(n) {
  const d = new Date(local)
  d.setDate(d.getDate() - n)
  return d.toLocaleDateString('en-CA').slice(0, 10)
}

const adsEnd = ref(todayISO)
const adsStart = ref(isoDaysAgo(29))
const adsPreset = ref('30d')

function setAdsPreset(p) {
  adsPreset.value = p
  if (p === '7d') {
    adsStart.value = isoDaysAgo(6); adsEnd.value = todayISO
  } else if (p === '30d') {
    adsStart.value = isoDaysAgo(29); adsEnd.value = todayISO
  } else if (p === 'month') {
    adsStart.value = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-01`
    adsEnd.value = todayISO
  }
  refreshAds()
}

function onAdsRangeChange() {
  adsPreset.value = 'custom'
  if (adsStart.value && adsEnd.value && adsStart.value > adsEnd.value) {
    const t = adsStart.value; adsStart.value = adsEnd.value; adsEnd.value = t
  }
  refreshAds()
}

function refreshAds() {
  fetchAds()
  fetchAdsInternal()
  fetchAdsServices()
  fetchAdsPrevious()
}

async function fetchAdsPrevious() {
  const base = apiBase()
  const days = Math.max(1, Math.round((Date.parse(adsEnd.value) - Date.parse(adsStart.value)) / 86400000) + 1)
  const end = shiftISO(adsStart.value, -1)
  const start = shiftISO(end, -(days - 1))
  const names = [...adsCampaigns.remolque, ...adsCampaigns.montacargas].map(c => c.name)
  try {
    const campaigns = await Promise.all(names.map(name =>
      axios.get(`${base}/dashboard/ads-campaign-metrics`, { params: { campaign: name, start, end } })
        .then(r => r.data)
        .catch(() => null)
    ))
    adsPrev.cost = campaigns.reduce((s, d) => s + Number(d && d.totals ? d.totals.cost : 0), 0)
    adsPrev.clicks = campaigns.reduce((s, d) => s + Number(d && d.totals ? d.totals.clicks : 0), 0)
    const internals = await Promise.all(Object.keys(adsInternalTypes).map(section =>
      axios.get(`${base}/dashboard/ads-internal-series`, {
        params: { types: adsInternalTypes[section], start, end },
      }).then(r => r.data).catch(() => null)
    ))
    adsPrev.services = internals.reduce((s, d) => s + Number(d && d.totals ? d.totals.services_closed : 0), 0)
    adsPrev.ganancia = internals.reduce((s, d) => s + Number(d && d.totals ? d.totals.ganancia : 0), 0)
  } catch (e) {
    adsPrev.cost = null
    adsPrev.clicks = null
    adsPrev.services = null
    adsPrev.ganancia = null
  }
}

async function fetchAds() {
  const base = apiBase()
  const names = [...adsCampaigns.remolque, ...adsCampaigns.montacargas].map(c => c.name)
  await Promise.all(names.map(async (name) => {
    if (!ads[name]) ads[name] = { loading: true, data: null }
    else ads[name].loading = true
    try {
      const r = await axios.get(`${base}/dashboard/ads-campaign-metrics`, {
        params: { campaign: name, start: adsStart.value, end: adsEnd.value },
      })
      ads[name].data = r.data
    } catch (e) {
      ads[name].data = {
        configured: false,
        error: 'No se pudo conectar con el servidor de anuncios.',
        totals: {}, series: [],
      }
    } finally {
      ads[name].loading = false
    }
  }))
}

async function fetchAdsServices() {
  const base = apiBase()
  adsServices.loading = true
  try {
    const r = await axios.get(`${base}/dashboard/ads-internal-services`, {
      params: { start: adsStart.value, end: adsEnd.value },
    })
    adsServices.data = r.data
  } catch (e) {
    adsServices.data = { error: 'No se pudo cargar la lista de servicios.', results: [], count: 0 }
  } finally {
    adsServices.loading = false
  }
}

async function fetchAdsInternal() {
  const base = apiBase()
  await Promise.all(Object.keys(adsInternalTypes).map(async (section) => {
    if (!adsInternal[section]) adsInternal[section] = { loading: true, data: null }
    else adsInternal[section].loading = true
    try {
      const r = await axios.get(`${base}/dashboard/ads-internal-series`, {
        params: { types: adsInternalTypes[section], start: adsStart.value, end: adsEnd.value },
      })
      adsInternal[section].data = r.data
    } catch (e) {
      adsInternal[section].data = {
        error: 'No se pudo cargar la información del sistema.',
        totals: {}, series: [],
      }
    } finally {
      adsInternal[section].loading = false
    }
  }))
}

const wsOps = useDashboardWS('operations')
const wsAcc = useDashboardWS('accounting')

let opsRefreshTimer = null
let accRefreshTimer = null
const refetchOps = () => { clearTimeout(opsRefreshTimer); opsRefreshTimer = setTimeout(fetchOperations, 400) }
const refetchAcc = () => { clearTimeout(accRefreshTimer); accRefreshTimer = setTimeout(fetchAccounting, 400) }

let fleetRefreshTimer = null
const refetchFleet = () => { clearTimeout(fleetRefreshTimer); fleetRefreshTimer = setTimeout(fetchFleet, 400) }

wsOps.onMessage(m => {
  if (['active_units_changed', 'orders_or_types_changed', 'orders_sucursal_changed'].includes(m.topic)) refetchOps()
  if (['active_units_changed', 'orders_or_types_changed', 'units_changed'].includes(m.topic)) refetchFleet()
})
wsAcc.onMessage(m => { if (m.topic === 'accounting_changed') refetchAcc() })

const wsClassOps = computed(() => ({ 'pill-open': wsOps.status === 'open', 'pill-retry': wsOps.status === 'retry' }))
const wsClassAcc = computed(() => ({ 'pill-open': wsAcc.status === 'open', 'pill-retry': wsAcc.status === 'retry' }))

onMounted(async () => { await fetchOperations(); await fetchFleet(); await fetchAccounting(); await fetchOpsMonthly(); await fetchRRHH(); await fetchMaintenance(); await fetchUnitStats(); await fetchOperationsMonthly(); await fetchRRHHMonthly(); await fetchCreditTimeseries(); await fetchUninvoicedSubtotalDaily(); await fetchPendingBillingByClient(); await fetchEstadisticoClientes(); refreshAds(); })
</script>

<style scoped>
/* ============================================================
   THEME TOKENS
   ============================================================ */
.dashboard-root {
  --row-h: 32px;
  --head-h: 40px;
  font-family: 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  position: relative;
  min-height: 100vh;
  padding: 18px 22px 48px;
  color: var(--text);
  background: var(--bg1);
  transition: background .35s ease, color .35s ease;
  overflow-x: hidden;
}

.theme-dark {
  color-scheme: dark;
  --bg1: #070b16;
  --bg2: #0b1224;
  --text: #e8edf6;
  --text-muted: #9aa7bd;
  --text-dim: #6b7891;
  --surface-solid: #121a2d;
  --border: rgba(148,163,184,.12);
  --border-strong: rgba(148,163,184,.22);
  --accent: #ffc434;
  --accent-2: #e6b12e;
  --accent-grad: #ffc434;
  --accent-grad-h: #ffc434;
  --card-bg: linear-gradient(180deg, rgba(21,29,48,.80), rgba(13,19,34,.80));
  --card-border: rgba(148,163,184,.14);
  --card-border-hover: rgba(255,196,52,.45);
  --card-shadow: 0 10px 30px rgba(2,6,18,.45);
  --card-shadow-hover: 0 18px 44px rgba(2,6,18,.62);
  --card-blur: blur(10px);
  --card-radius: 16px;
  --card-pad: 14px;
  --card-title: #e8edf6;
  --card-topline: linear-gradient(90deg, transparent, rgba(255,196,52,.55), transparent);
  --input-bg: rgba(9,14,28,.7);
  --input-border: rgba(148,163,184,.20);
  --table-head-bg: rgba(11,17,33,.96);
  --head-bg: linear-gradient(180deg, rgba(255,196,52,.12), rgba(255,196,52,.03));
  --row-even: rgba(148,163,184,.05);
  --row-hover: rgba(255,196,52,.08);
  --kpi-bg: rgba(30,41,59,.42);
  --glass: rgba(15,22,40,.66);
}

.theme-light {
  color-scheme: light;
  --bg1: #eaeef6;
  --bg2: #f6f8fc;
  --text: #0f172a;
  --text-muted: #475569;
  --text-dim: #94a3b8;
  --surface-solid: #ffffff;
  --border: rgba(15,23,42,.08);
  --border-strong: rgba(15,23,42,.14);
  --accent: #9a7209;
  --accent-2: #9a7209;
  --accent-grad: #9a7209;
  --accent-grad-h: #9a7209;
  --card-bg: rgba(255,255,255,.90);
  --card-border: rgba(15,23,42,.08);
  --card-border-hover: rgba(154,114,9,.40);
  --card-shadow: 0 6px 20px rgba(15,23,42,.07);
  --card-shadow-hover: 0 16px 38px rgba(15,23,42,.14);
  --card-blur: blur(6px);
  --card-radius: 16px;
  --card-pad: 14px;
  --card-title: #0f172a;
  --card-topline: linear-gradient(90deg, transparent, rgba(154,114,9,.45), transparent);
  --input-bg: #ffffff;
  --input-border: #d8dde6;
  --table-head-bg: rgba(255,255,255,.97);
  --head-bg: linear-gradient(180deg, rgba(154,114,9,.10), rgba(154,114,9,.02));
  --row-even: rgba(15,23,42,.03);
  --row-hover: rgba(154,114,9,.07);
  --kpi-bg: #f8fafc;
  --glass: rgba(255,255,255,.72);
}

/* Ambient backdrop */
.ambient {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.theme-dark .ambient {
  background:
    radial-gradient(900px 520px at 12% -8%, rgba(255,196,52,.10), transparent 60%),
    linear-gradient(180deg, #070b16 0%, #0a1020 100%);
}
.theme-dark .ambient::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(148,163,184,.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148,163,184,.05) 1px, transparent 1px);
  background-size: 42px 42px;
  -webkit-mask-image: radial-gradient(ellipse at 50% 0%, #000 30%, transparent 80%);
  mask-image: radial-gradient(ellipse at 50% 0%, #000 30%, transparent 80%);
}
.theme-light .ambient {
  background:
    radial-gradient(900px 500px at 10% -10%, rgba(255,196,52,.16), transparent 60%),
    linear-gradient(180deg, #eaeef6 0%, #f4f7fc 100%);
}

.dashboard-root > *:not(.ambient) { position: relative; z-index: 1; }

/* ============================================================
   HEADER
   ============================================================ */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-radius: 18px;
  background: var(--glass);
  border: 1px solid var(--border);
  box-shadow: var(--card-shadow);
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
}

.brand { display: flex; align-items: center; gap: 12px; cursor: pointer; }
.logo {
  height: 38px;
  filter: drop-shadow(0 4px 12px rgba(255,196,52,.28));
  transition: transform .2s ease;
}
.brand:hover .logo { transform: scale(1.04) rotate(-2deg); }
.brand-text { display: flex; flex-direction: column; line-height: 1.15; }
.brand-text h1 {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: .3px;
  background: var(--accent-grad-h);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.brand-sub { font-size: 11px; color: var(--text-muted); letter-spacing: .4px; }

.header-right { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

.clock {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}
.clock-time {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 1px;
  color: var(--text);
}
.clock-date { font-size: 10.5px; color: var(--text-muted); text-transform: capitalize; }

.status-cluster { display: flex; gap: 8px; flex-wrap: wrap; }
.ws-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--kpi-bg);
  border: 1px solid var(--border);
  color: var(--text-muted);
  text-transform: capitalize;
}
.ws-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #94a3b8;
  box-shadow: 0 0 0 0 rgba(148,163,184,.5);
}
.pill-open { color: #34d399; border-color: rgba(52,211,153,.35); }
.pill-open .ws-dot { background: #34d399; animation: pulse-dot 1.8s infinite; }
.pill-retry { color: #fbbf24; border-color: rgba(251,191,36,.35); }
.pill-retry .ws-dot { background: #fbbf24; animation: pulse-dot 1s infinite; }
@keyframes pulse-dot {
  0% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  70% { box-shadow: 0 0 0 6px transparent; opacity: .85; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
}

.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px; height: 38px;
  border-radius: 12px;
  border: 1px solid var(--border-strong);
  background: var(--kpi-bg);
  color: var(--text);
  cursor: pointer;
  transition: transform .18s ease, color .18s ease, border-color .18s ease, background .18s ease;
}
.theme-toggle svg { width: 18px; height: 18px; }
.theme-toggle:hover { color: var(--accent); border-color: var(--accent); transform: translateY(-1px) rotate(12deg); }

/* ============================================================
   TABS
   ============================================================ */
.tabs {
  display: flex;
  gap: 6px;
  margin: 16px 0;
  flex-wrap: wrap;
  padding: 6px;
  border-radius: 16px;
  background: var(--glass);
  border: 1px solid var(--border);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  width: fit-content;
  max-width: 100%;
}
.tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease;
}
.tab .tab-ico { width: 16px; height: 16px; opacity: .9; }
.tab:hover { color: var(--text); background: var(--row-hover); }
.tab.active {
  color: #1c1404;
  background: #ffc434;
  box-shadow: 0 8px 22px rgba(255,196,52,.28);
  transform: translateY(-1px);
}
.theme-light .tab.active {
  color: #fff;
  background: #9a7209;
  box-shadow: 0 8px 20px rgba(154,114,9,.22);
}

/* ============================================================
   TAB BODY / CONTROLS
   ============================================================ */
.tab-body { display: flex; flex-direction: column; gap: 14px; }

.controls {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--text-muted);
  padding: 12px 16px;
  border-radius: 14px;
  background: var(--glass);
  border: 1px solid var(--border);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}
.controls label { display: inline-flex; align-items: center; gap: 8px; font-weight: 500; }
.controls input[type="date"],
.controls input[type="month"],
.controls input[type="number"],
.controls select,
.inline-control input {
  padding: 7px 10px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 9px;
  width: auto;
  accent-color: var(--accent);
  transition: border-color .15s ease, box-shadow .15s ease;
}
.controls input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(255,196,52,.28);
}

/* ============================================================
   HERO KPI STRIP
   ============================================================ */
.hero-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}
.hero-tile {
  position: relative;
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  -webkit-backdrop-filter: var(--card-blur);
  backdrop-filter: var(--card-blur);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.hero-tile::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: var(--accent-grad);
}
.hero-tile:hover {
  transform: translateY(-3px);
  box-shadow: var(--card-shadow-hover);
  border-color: var(--card-border-hover);
}
.hero-tile:nth-child(2)::before,
.hero-tile:nth-child(3)::before,
.hero-tile:nth-child(4)::before { background: var(--accent); }

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) { .kpi-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) { .kpi-strip { grid-template-columns: 1fr; } }

.kpi-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 132px;
  padding: 14px 14px 10px;
  border-radius: 16px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  overflow: hidden;
}
.kpi-card::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: var(--accent);
}
.kpi-card.is-alert {
  border-color: rgba(220, 38, 38, .45);
}
.kpi-card.is-alert::before { background: #dc2626; }
.kpi-card__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.kpi-card__label {
  font-size: 11px;
  font-weight: 650;
  letter-spacing: .4px;
  text-transform: uppercase;
  color: var(--text-muted);
}
.kpi-card__flag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .3px;
  text-transform: uppercase;
  color: #dc2626;
}
.kpi-card__value {
  font-size: 26px;
  font-weight: 750;
  line-height: 1.1;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
.kpi-card__secondary {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
.kpi-card__meta { display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: baseline; }
.kpi-delta { font-size: 13px; font-weight: 700; color: var(--text); font-variant-numeric: tabular-nums; }
.kpi-delta.up, .kpi-hint.up { color: #15803d; }
.kpi-delta.down, .kpi-hint.down { color: #b91c1c; }
.theme-dark .kpi-delta.up, .theme-dark .kpi-hint.up { color: #4ade80; }
.theme-dark .kpi-delta.down, .theme-dark .kpi-hint.down { color: #f87171; }
.kpi-strip--three { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.kpi-strip--aging { grid-template-columns: repeat(5, minmax(0, 1fr)); }
@media (max-width: 1100px) {
  .kpi-strip--three, .kpi-strip--aging { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
}
.capture-board { display: flex; flex-direction: column; gap: 10px; }
.capture-board h2 { margin: 0; font-size: 16px; font-weight: 700; }
.shift-head td {
  background: var(--kpi-bg);
  font-weight: 700;
  letter-spacing: .3px;
  text-transform: uppercase;
  font-size: 11px;
  color: var(--text-muted);
}

.fleet { display: flex; flex-direction: column; gap: 10px; }
.fleet__bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.fleet__bar h2 { margin: 0; font-size: 16px; font-weight: 700; }
.fleet__boards { display: flex; gap: 6px; }
.fleet__boards button {
  border: 1px solid var(--border);
  background: var(--kpi-bg);
  color: var(--text-muted);
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}
.fleet__boards button.active { background: var(--accent); color: #1c1404; border-color: transparent; }
.theme-light .fleet__boards button.active { color: #fff; }
.fleet__boards span { margin-left: 6px; font-variant-numeric: tabular-nums; }
.fleet__board {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}
.fleet__col {
  --fleet: #6b7280;
  border: 1px solid var(--card-border);
  border-radius: 14px;
  background: var(--card-bg);
  min-width: 0;
}
.fleet__col--libre { --fleet: #16a34a; }
.fleet__col--en_servicio { --fleet: #1d4ed8; }
.fleet__col--fuera { --fleet: #6b7280; }
.fleet__col--mantenimiento { --fleet: #d97706; }
.fleet__col header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--fleet) 14%, transparent);
}
.fleet__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--fleet);
  flex: 0 0 auto;
}
.fleet__title { flex: 1; }
.fleet__col header b {
  min-width: 26px;
  text-align: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--fleet);
  color: #fff;
  font-size: 12px;
}
.fleet__list { display: flex; flex-direction: column; gap: 8px; padding: 10px; max-height: 640px; overflow: auto; }
.fleet__empty { margin: 8px 0; text-align: center; }
.fleet-card {
  border: 1px solid var(--border);
  border-left: 4px solid var(--fleet);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--kpi-bg);
}
.fleet-card--alert {
  border-color: rgba(220, 38, 38, .55);
  border-left-color: #dc2626;
  animation: fleet-blink-red 1.3s ease-in-out infinite;
}
@keyframes fleet-blink-red {
  0%, 100% { background: var(--kpi-bg); box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
  50% { background: rgba(220, 38, 38, .22); box-shadow: 0 0 0 4px rgba(220, 38, 38, .18); }
}
@media (prefers-reduced-motion: reduce) {
  .fleet-card--alert { animation: none; background: rgba(220, 38, 38, .14); }
}
.fleet-card__top { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
.fleet-card__top strong { font-size: 15px; }
.fleet-card__top span, .fleet-card p { margin: 4px 0 0; font-size: 12px; color: var(--text-muted); }
.fleet-card__svc a { color: var(--text); font-weight: 650; }
.fleet-card__time { color: var(--text); }
.fleet-card__time--alert { color: #dc2626; font-weight: 700; }
.theme-dark .fleet-card__time--alert { color: #f87171; }
@media (max-width: 1100px) { .fleet__board { grid-template-columns: 1fr 1fr; } }
@media (max-width: 700px) { .fleet__board { grid-template-columns: 1fr; } }

.att-board { display: flex; flex-direction: column; gap: 10px; }
.att-board__bar { display: flex; align-items: center; gap: 12px; }
.att-board__bar h2 { margin: 0; font-size: 16px; font-weight: 700; }
.att-tabs { display: flex; flex-wrap: wrap; gap: 8px; }
.att-tab {
  border: 1px solid var(--border);
  background: var(--kpi-bg);
  color: #1e3a8a;
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}
.theme-dark .att-tab { color: #bfdbfe; }
.att-tab.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.att-wrap { overflow: auto; max-height: 560px; border: 1px solid var(--border); border-radius: 12px; }
.att-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
.att-table th, .att-table td { padding: 6px 8px; border-bottom: 1px solid var(--border); white-space: nowrap; background: var(--card-bg); }
.att-table thead th { position: sticky; top: 0; z-index: 2; background: var(--table-head-bg); }
.att-table .sticky-col { position: sticky; left: 0; z-index: 1; text-align: left; background: var(--table-head-bg); }
.att-table thead .sticky-col { z-index: 3; }
.att-pill {
  display: inline-flex;
  min-width: 22px;
  justify-content: center;
  border-radius: 6px;
  padding: 2px 5px;
  font-weight: 700;
  font-size: 11px;
}
.att-pill.status-empty { background: #ffffff; color: #334155; }
.att-pill.status-a { background: #dcfce7; color: #166534; }
.att-pill.status-f { background: #fee2e2; color: #991b1b; }
.att-pill.status-r { background: #fef9c3; color: #854d0e; }
.att-pill.status-d { background: #dbeafe; color: #1e40af; }
.att-pill.status-t { background: #ede9fe; color: #5b21b6; }
.att-pill.status-s { background: #fecaca; color: #7f1d1d; }
.att-pill.status-v { background: #ecfccb; color: #3f6212; }
.att-pill.status-p { background: #cffafe; color: #0e7490; }
.att-pill.status-i { background: #fed7aa; color: #9a3412; }
.theme-dark .att-pill.status-empty { background: #1e293b; color: #cbd5e1; }
.att-legend { display: flex; flex-wrap: wrap; gap: 8px 14px; font-size: 12px; color: var(--text-muted); }
.att-legend__item { display: inline-flex; align-items: center; gap: 6px; }
.kpi-spark { height: 36px; margin-top: auto; }

.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 900px) { .chart-grid { grid-template-columns: 1fr; } }

.fold {
  border: 1px solid var(--card-border);
  border-radius: 14px;
  background: var(--card-bg);
  padding: 0 14px 12px;
}
.fold > summary {
  cursor: pointer;
  list-style: none;
  padding: 12px 0;
  font-size: 13.5px;
  font-weight: 650;
  color: var(--card-title);
}
.fold > summary::-webkit-details-marker { display: none; }
.fold-inner { background: transparent; border: none; padding: 0; margin-top: 8px; }
.fold-flag, .status-flag, .quality-flag {
  color: #b45309;
  font-size: 12px;
  font-weight: 650;
}
.fold-flag { margin-left: 8px; }
.quality-flag { margin: 0 0 8px; }
.quality-flag span { margin-right: 8px; }
.row-stale td { background: rgba(220, 38, 38, .12); }
.inline-control { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; }
.hero-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .6px;
  text-transform: uppercase;
  color: var(--text-muted);
}
.hero-value {
  display: block;
  margin-top: 6px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: .3px;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap:10px; }

.list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:6px; }
.list li { display:flex; justify-content:space-between; align-items:center; font-size:13px; padding:3px 0; border-bottom:1px dashed var(--border); }
.list li:last-child { border-bottom:none; }
.name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width: 70%; color: var(--text); }
.right { font-weight:700; color: var(--text); }
.muted { color: var(--text-dim); font-size:12px; }

.table-wrap { overflow:auto; border:1px solid var(--border); border-radius:10px; }
.table-wrap table { width:100%; border-collapse:collapse; font-size:12px; color: var(--text); }
.table-wrap th, .table-wrap td { padding:7px 9px; border-bottom:1px solid var(--border); text-align:left; }
.table-wrap td.num, .table-wrap th.num { text-align:right; }
.table-wrap tbody tr { transition: background .12s ease; }
.table-wrap tbody tr:hover td { background: var(--row-hover); }

.kpis { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:12px; }
.kpi { background: var(--kpi-bg); border:1px solid var(--border); border-radius:12px; padding:10px 12px; min-width:84px; text-align:center; }
.kpi-label { font-size:11px; color: var(--text-muted); }
.kpi-value { font-weight:700; font-size:16px; color: var(--text); }
.single-kpi {
  font-size:34px; font-weight:800; text-align:center; padding:14px;
  background: var(--accent-grad-h);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  font-variant-numeric: tabular-nums;
}

.placeholder { color: var(--text-dim); font-size:13px; padding:16px; border:1px dashed var(--border-strong); border-radius:12px; background: var(--kpi-bg); }

/* generic scrolling wrapper for up to 15 rows */
.scroll-rows-15 {
  max-height: calc(15 * var(--row-h) + var(--head-h));
  overflow: auto;
}

/* tables: keep header visible while body scrolls */
.table-wrap table {
  border-collapse: collapse;
  width: 100%;
}
.table-wrap thead th {
  position: sticky;
  top: 0;
  background: var(--table-head-bg);
  color: var(--text-muted);
  font-weight: 700;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  z-index: 1;
}

/* optional: for <ol>/<ul> lists */
.list.scroll-rows-15 {
  max-height: calc(15 * var(--row-h));
  overflow: auto;
}


/* Desktop masonry: 3 columns; tablet: 2; mobile: 1 */
.masonry {
  column-count: 3;
  column-gap: 1rem;
}
@media (max-width: 900px) { .masonry { column-count: 2; } }
@media (max-width: 650px) { .masonry { column-count: 1; } }


/* Each card must be inline-block so it respects column flow */

/* Masonry items – MUST be block for columns to form correctly */
.masonry :deep(.stat-card) {
  display: block;                    /* <- key: block, not inline-block */
  margin: 0 0 1rem;
  break-inside: avoid-column;        /* standard */
  -webkit-column-break-inside: avoid;/* Safari/Chrome */
  -moz-column-break-inside: avoid;   /* Firefox */
  width: auto;                       /* don’t force a width that fights columns */
  max-width: none;
}


/* keep your scroll cap util working inside cards */
.scroll-rows-15 { max-height: calc(15 * var(--row-h) + var(--head-h)); overflow: auto; }

.legend-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
  font-size: 12px;
  list-style: none;
  padding-left: 0;
}
.legend-wrap li { display: flex; align-items: center; gap: 6px; color: var(--text-muted); }
.swatch { width: 10px; height: 10px; border-radius: 3px; border: 1px solid var(--border-strong); }
.legend-name { white-space: nowrap; }
.legend-val { color: var(--text-dim); }

/* highlight rows */
.best  td { background: rgba(34,197,94,.16) !important; }
.worst td { background: rgba(239,68,68,.16) !important; }


.rrhh-monthly-layout {
  display: grid;
  grid-template-columns: minmax(220px, 1.1fr) minmax(240px, 1.5fr);
  gap: 10px;
}

/* stack vertically on narrower screens */
@media (max-width: 900px) {
  .rrhh-monthly-layout {
    grid-template-columns: 1fr;
  }
}

.rrhh-monthly-table {
  display: flex;
  flex-direction: column;
}

.table-header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.link-button {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
}

.link-button:hover {
  text-decoration: underline;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover td {
  background: var(--row-hover);
}

.row-selected td {
  background: rgba(255,196,52,.16);
}

.row-selected:hover td {
  background: rgba(255,196,52,.24);
}



/* card is 100% width of dashboard */
.rrhh-wide-card {
  width: 80% !important;
  margin-top: 12px;
}

/* 2-column layout inside */
.rrhh-monthly-layout {
  display: grid;
  grid-template-columns: 320px 1fr; /* left table fixed, right chart expands */
  gap: 16px;
  width: 100%;
}

/* mobile */
@media (max-width: 900px) {
  .rrhh-monthly-layout {
    grid-template-columns: 1fr;
  }
}

/* chart: stretch */
.rrhh-monthly-chart {
  width: 100%;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rrhh-matrix-wrap {
  max-width: 100%;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface-solid);
}

/* ── Shared base for RRHH + Estadístico data tables ── */
.rrhh-matrix-table,
.estadistico-table {
  border-collapse: separate;
  border-spacing: 0;
  font-variant-numeric: tabular-nums;
}

/* elegant, single-hue sticky headers (no rainbow gradient) */
.rrhh-matrix-table thead th,
.estadistico-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--head-bg), var(--surface-solid);
  color: var(--text-muted);
  font-weight: 700;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: .6px;
  border-bottom: 2px solid var(--accent);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}
.rrhh-matrix-table thead .sticky-col,
.estadistico-table thead .sticky-col {
  z-index: 3;
  background: var(--head-bg), var(--surface-solid);
}

/* zebra + hover */
.rrhh-matrix-table tbody tr:nth-child(even) td,
.estadistico-table tbody tr:nth-child(even) td {
  background-color: var(--row-even);
}
.rrhh-matrix-table tbody tr:hover td,
.estadistico-table tbody tr:hover td {
  background-color: var(--row-hover);
}

/* frozen first column */
.rrhh-matrix-table .sticky-col,
.estadistico-table .sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--surface-solid);
  box-shadow: 10px 0 16px -12px rgba(2, 6, 23, .45);
}
.rrhh-matrix-table tbody tr:nth-child(even) .sticky-col,
.estadistico-table tbody tr:nth-child(even) .sticky-col {
  background: var(--surface-solid);
}
.rrhh-matrix-table tbody tr:hover .sticky-col,
.estadistico-table tbody tr:hover .sticky-col {
  background: var(--surface-solid);
  color: var(--accent);
}

/* ── RRHH matrix: a clean heatmap; may scroll horizontally ── */
.rrhh-matrix-table {
  width: max-content;
  min-width: 100%;
  font-size: 12px;
}
.rrhh-matrix-table th,
.rrhh-matrix-table td {
  padding: 7px 9px;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
  color: var(--text);
  transition: background .12s ease, color .12s ease;
}
.rrhh-matrix-table .num { text-align: center; min-width: 34px; }
.rrhh-matrix-table .day-cell { border-radius: 6px; }

/* ── Estadístico tables: numbers always on a single line; client column kept slim ── */
.estadistico-table {
  width: max-content;
  min-width: 100%;
  font-size: 11px;
}
.estadistico-table th,
.estadistico-table td {
  padding: 5px 9px;
  border-bottom: 1px solid var(--border);
  color: var(--text);
  text-align: center;
  white-space: nowrap;          /* numbers display freely, never wrap */
  transition: background .12s ease, color .12s ease;
}
/* slim client column; long names wrap inside it instead of widening the table */
.estadistico-table .sticky-col {
  width: 132px;
  max-width: 132px;
  text-align: left;
  font-weight: 600;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.2;
}

.estadistico-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.estadistico-table-wrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface-solid);
}

.estadistico-table .total-label {
  color: var(--accent);
  font-weight: 800;
  letter-spacing: .3px;
}

/* serious, uniform clerk names with a small color cue that maps to the line chart */
.clerk-name {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: .2px;
}
.clerk-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex: 0 0 auto;
  box-shadow: 0 0 0 2px var(--surface-solid), 0 0 0 3px var(--border);
}

.day-cell,
.total-cell {
  text-align: center;
}

/* accent the row/column totals */
.total-cell {
  font-weight: 800;
  color: var(--accent);
  background: linear-gradient(180deg, rgba(56, 189, 248, .14), rgba(99, 102, 241, .14)) !important;
}
.theme-light .total-cell {
  background: linear-gradient(180deg, rgba(2, 132, 199, .10), rgba(99, 102, 241, .10)) !important;
}

.rrhh-monthly-chart :deep(.echarts) {
  width: 100% !important;
  height: 320px !important;
}

/* ── Publicidad (Google Ads) ── */
.ads-controls { align-items: center; }
.ads-presets { display: inline-flex; gap: 6px; margin-left: auto; flex-wrap: wrap; }
.ads-preset {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--kpi-bg);
  color: var(--text-muted);
  cursor: pointer;
  transition: color .15s ease, background .15s ease, border-color .15s ease;
}
.ads-preset:hover { color: var(--text); border-color: var(--accent); }
.ads-preset.active {
  color: #fff;
  background: var(--accent-grad-h);
  border-color: transparent;
}

.ads-group { display: flex; flex-direction: column; gap: 14px; }
.ads-group + .ads-group { margin-top: 6px; }
.ads-group-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 2px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: .3px;
  color: var(--text);
}
.ads-group-bar {
  width: 6px;
  height: 20px;
  border-radius: 999px;
  background: var(--accent);
}
.ads-group-bar.montacargas { background: #64748b; }

.ads-services-table { max-height: calc(15 * var(--row-h) + var(--head-h)); }
.ads-type-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.ads-type-chip.remo { color: #0284c7; background: rgba(2, 132, 199, .12); border-color: rgba(2, 132, 199, .30); }
.ads-type-chip.mont { color: #d97706; background: rgba(217, 119, 6, .12); border-color: rgba(217, 119, 6, .30); }
.theme-dark .ads-type-chip.remo { color: #7dd3fc; }
.theme-dark .ads-type-chip.mont { color: #fbbf24; }

.ads-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 8px;
  color: #fff;
  background: var(--accent-grad-h);
  border: 1px solid transparent;
  transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease;
}
.ads-open-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px -8px var(--accent); opacity: .95; }
.ads-open-btn svg { width: 13px; height: 13px; }

/* ============================================================
   SUB-TABS (sections inside a tab)
   ============================================================ */
.subtabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 5px;
  border-radius: 14px;
  background: var(--glass);
  border: 1px solid var(--border);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  width: fit-content;
  max-width: 100%;
}
.subtab {
  padding: 7px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: color .18s ease, background .18s ease, box-shadow .18s ease;
}
.subtab:hover { color: var(--text); background: var(--row-hover); }
.subtab.active {
  color: #fff;
  background: var(--accent-grad-h);
  box-shadow: 0 6px 16px -8px var(--accent);
}

/* ============================================================
   UNIDADES: per-unit totals board (category columns)
   ============================================================ */
.controls .unit-sort {
  padding: 7px 10px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 9px;
  font-weight: 600;
  cursor: pointer;
}
.controls .unit-sort:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(255,196,52,.28);
}
.controls .unit-toggle { gap: 6px; cursor: pointer; }
.controls .unit-toggle input { accent-color: var(--accent); cursor: pointer; }

/* Category selector pills */
.unit-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.unit-pill {
  --col-accent: var(--accent);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--kpi-bg);
  color: var(--text-muted);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: color .18s ease, background .18s ease, border-color .18s ease, box-shadow .18s ease;
}
.unit-pill--ab { --col-accent: var(--accent); }
.unit-pill--cd { --col-accent: #64748b; }
.unit-pill--montacargas { --col-accent: #0369a1; }
.unit-pill--otro { --col-accent: #a78bfa; }

.unit-pill:hover { color: var(--text); border-color: var(--col-accent); }
.unit-pill.active {
  color: var(--text);
  border-color: var(--col-accent);
  background: color-mix(in srgb, var(--col-accent) 16%, transparent);
  box-shadow: 0 6px 16px -10px var(--col-accent);
}
.unit-pill__dot {
  width: 9px; height: 9px;
  border-radius: 999px;
  background: var(--col-accent);
  flex: 0 0 auto;
}
.unit-pill__count {
  min-width: 22px;
  text-align: center;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--col-accent);
  color: #06101f;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

/* Card grid: pack as many cards per row as the viewport allows */
.unit-grid {
  --col-accent: var(--accent);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(222px, 1fr));
  gap: 10px;
  align-items: start;
}
.unit-grid--ab { --col-accent: var(--accent); }
.unit-grid--cd { --col-accent: #64748b; }
.unit-grid--montacargas { --col-accent: #0369a1; }
.unit-grid--otro { --col-accent: #a78bfa; }

.unit-grid__empty {
  grid-column: 1 / -1;
  padding: 26px 0;
  text-align: center;
  color: var(--text-dim);
  font-size: 12.5px;
  border: 1px dashed var(--border-strong);
  border-radius: 12px;
}

.unit-stat-card {
  display: grid;
  gap: 8px;
  padding: 9px 11px;
  border-radius: 11px;
  border: 1px solid var(--border);
  border-left: 4px solid var(--col-accent);
  background: var(--kpi-bg);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.unit-stat-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--card-shadow);
  border-color: var(--border-strong);
  border-left-color: var(--col-accent);
}

.unit-stat-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.unit-stat-card__id {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: .02em;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
.unit-stat-card__cat {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .5px;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
}

/* Single row, never clipped and never wrapped: each metric sizes to its own
   content and the grid track below is wide enough for the widest figures. */
.unit-stat-card__metrics {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  gap: 10px;
}
.unit-metric {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 0 0 auto;
}
.unit-metric__label {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: .5px;
  text-transform: uppercase;
  color: var(--text-dim);
}
.unit-metric__value {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.unit-stat-card__last {
  display: flex;
  align-items: center;
  gap: 5px;
  padding-top: 6px;
  border-top: 1px solid var(--border);
  font-size: 10.5px;
  line-height: 1.25;
  color: var(--text-dim);
}
.unit-stat-card__last svg { flex: 0 0 auto; }
.unit-stat-card__last--alert { color: #f87171; font-weight: 700; }
.theme-light .unit-stat-card__last--alert { color: #dc2626; }
.unit-stat-card:has(.unit-stat-card__last--alert) { border-left-color: #ef4444; }

</style>
